/**
 * ta-chart.js — TA chart engine (Lightweight Charts v4.2)
 *
 * Public API:
 *   var engine = TaChart.create(chartEl, tooltipEl, {
 *     tf: '1h'|'D1',
 *     onMarkerClick: function(triggers) { ... }   // массив объектов триггеров
 *   });
 *   engine.load(ticker, tf);
 *   engine.toggleLayer('sr');
 *   engine.getTrigSideHtml()           — HTML для правой панели (desktop)
 *   engine.getRegimeBadgeHtml()        — HTML для компактного badge'а в шапке (mobile)
 *   engine.renderTriggerBlocks(arr)    — HTML списка триггеров (для bottom sheet)
 *   engine.addSoloTrigger(t)         — триггер из строки /setups, которого нет в chart-data (4ч на младшем ТФ)
 *   engine.destroy();
 *   TaChart.fmtPrice(p)                — формат цены входа (общий с витриной /setups)
 */
var TaChart = (function() {
  // Отступ справа при ОТКРЫТИИ графика: сколько пустых баров между последним
  // баром и ценовой шкалой (было 20, с 2026-07-22 — 10). Потребители, которые
  // после load() зовут setVisibleBars/setVisibleDays, задают свой padding сами
  // (например /setups → setVisibleBars(N, 8)) — на них константа не влияет.
  var RIGHT_OFFSET_BARS = 10;
  'use strict';

  // Формат цены входа: ≥1 → 2 знака, мелкие пенни-тикеры — больше. Один на
  // подпись «вход» панели графика и на цену входа в строке витрины /setups
  // (экспорт TaChart.fmtPrice, 11.09.2026): иначе строка и график округляли бы
  // одну и ту же цену по-разному.
  function _fmtPrice(p) {
    if (p == null || isNaN(p)) return '';
    var a = Math.abs(p);
    if (a >= 1) return p.toFixed(2);
    if (a >= 0.01) return p.toFixed(4);
    return p.toFixed(6);
  }

  var _TRIGGER_RU = {
    // Актуальные MTF-типы (production, 5 триггеров)
    'FALSE_BREAKOUT_MTF': 'Ложный пробой',
    'STOP_HUNT_MTF':      'Сбор ликвидности',
    'VSA_MTF':            'Поглощение объёмом',
    'DIVERGENCE':         'Дивергенция',   // fallback; точный вид (Бычья/Скрытая медвежья…) приходит в t.tip_title
    'VWAP_REV_MTF':       'Возврат к VWAP',
    'CLIMAX_MTF':         'Кульминация объёма',
    'CLEAN_BOUNCE_MTF':   'Отбой от уровня',
    'DOUBLE_EXTREME_MTF': 'Двойная вершина/дно',  // точный вид (дно/вершина) в t.tip_title
    'HIDDEN_LEVEL':       'Скрытый уровень',
    'IMPULSE_BACK':      'Откат к импульсу',
    'IMPULSE_BACK_LATE': 'Поздний откат к импульсу',
    // Legacy v6 (на графике больше не эмитятся, оставлены для истории)
    'STOP_HUNT':        'Сбор ликвидности',
    'FALSE_BREAKOUT':   'Ложный пробой',
    'CLEAN_BOUNCE':     'Отбой от уровня',
    'COMPRESSION':      'Сжатие',
    'VOLUME_CLIMAX':    'Кульминация объёма',
    'BOS':              'Слом структуры',
    'PULLBACK':         'Откат к EMA',
    'VWAP_REVERSION':   'Возврат к VWAP',
    'BREAKOUT_RETEST':  'Пробой + ретест',
    'INSIDE_BAR':       'Внутренний бар'
  };

  var _REGIME_LABEL = {
    'TREND_UP':    'Восходящий тренд',
    'TREND_DOWN':  'Нисходящий тренд',
    'HYBRID_UP':   'Мягкий восходящий',
    'HYBRID_DOWN': 'Мягкий нисходящий',
    'RANGE':       'Флэт',
    'SHOCK':       'Шоковое движение'
  };
  var _REGIME_CLS = {
    'TREND_UP':    'green',
    'TREND_DOWN':  'red',
    'HYBRID_UP':   'gold',
    'HYBRID_DOWN': 'red',
    'RANGE':       'muted',
    'SHOCK':       'gold'
  };

  var _QUALITY_DOT = {
    'solid':    '#3fb84f',
    'small_n':  '#d4a016',
    'fragile':  '#ff9800'
  };
  var _QUALITY_LABEL = {
    'solid':    'solid (валидированная стратегия)',
    'small_n':  'small N (мало наблюдений в выборке)',
    'fragile':  'fragile (близко к порогам отсечения)'
  };

  function _esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
  // "2026-04-24 14:00:00" → "14:00"
  function _fmtBarTime(s) {
    if (!s) return '';
    var parts = s.replace('T', ' ').split(' ');
    return parts.length >= 2 ? parts[1].substring(0, 5) : '';
  }
  // "2026-04-24 ..." → "24 апреля"
  var _RU_MONTHS = ['января','февраля','марта','апреля','мая','июня',
                    'июля','августа','сентября','октября','ноября','декабря'];
  function _fmtRuDate(isoDate) {
    if (!isoDate) return '';
    var dpart = isoDate.split(' ')[0];
    var parts = dpart.split('-');
    if (parts.length !== 3) return '';
    var m = parseInt(parts[1], 10) - 1;
    if (m < 0 || m > 11) return '';
    return parseInt(parts[2], 10) + ' ' + _RU_MONTHS[m];
  }
  // detail = "Ложный пробой S 1,518 — прокол 1.1 ATR, возврат за 1 св."
  //   → {title:"Ложный пробой S 1,518", body:"прокол 1.1 ATR, возврат за 1 св."}
  // Поддержка обоих тире: U+2014 (—) и U+2013 (–).
  function _parseDetail(detail) {
    if (!detail) return {title: '', body: ''};
    var dashIdx = detail.indexOf('—');
    if (dashIdx < 0) dashIdx = detail.indexOf('–');
    if (dashIdx < 0) return {title: detail.trim(), body: ''};
    return {
      title: detail.substring(0, dashIdx).trim(),
      body:  detail.substring(dashIdx + 1).trim()
    };
  }
  // Подбор цвета текста по фону для контраста (luma).
  function _textOn(bg) {
    if (!bg || bg.charAt(0) !== '#' || bg.length < 7) return '#0c1116';
    var r = parseInt(bg.substr(1, 2), 16);
    var g = parseInt(bg.substr(3, 2), 16);
    var b = parseInt(bg.substr(5, 2), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return '#0c1116';
    var luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luma > 140 ? '#0c1116' : '#fff';
  }

  // Открытие утренней сессии MOEX (МСК). Свечи в payload — MSK wall-clock под
  // меткой UTC, поэтому сравниваем именно getUTCHours().
  var MSK_SESSION_OPEN_H = 7;
  var _MON_RU = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн',
                 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

  function create(chartEl, tooltipEl, opts) {
    opts = opts || {};
    var state = {
      chart: null,
      candleSeries: null,
      volumeSeries: null,
      rsiSeries: null,
      ema50Series: null,
      ema200Series: null,
      priceLinesArr: [],
      _mtfLines: [],   // MTF-линии отдельно от priceLinesArr — чтобы перерисовывать при смене подсветки
      divergenceSeries: [],  // line series для divergence visualization (price + RSI panes)
      selectedDivergenceKey: null,  // bar_date selected trig (для toggle off на повторный click)
      bearishFlagSeries: [],  // line series для bearish_flag (древко + границы флага, click-to-inspect)
      selectedFlagKey: null,
      deSeriesByKey: {},    // {bar_date → [line series]} для double bottom/top (зигзаг + линия шеи)
      deHiddenKeys: {},     // {bar_date → 1} фигуры, скрытые кликом (по умолчанию все показаны)
      triggersData: [],
      exitMarkers: [],
      soloKey: null,      // {bar_date, trigger, direction, tf} — solo-режим /setups: показываем
                          // ТОЛЬКО этот сетап (вход+выход), точно по цене. null = все маркеры (/ta).
      soloSeries: [],     // прозрачные line-серии для price-anchored solo-маркеров
      extraMarker: null,  // доп. маркер (setExtraMarker) — для /ml-setups: показать
                          // кликнутый ре-детект-триггер, которого нет в trigger_sent_log
                          // (excluded-ячейки не эмитятся). Для /setups всегда null.
      livePriceLine: null,
      liveInterval: null,
      lastData: null,
      currentTf: opts.tf || '1h',
      currentTicker: null,
      onMarkerClick: opts.onMarkerClick || null,
      // База URL для chart-data. По умолчанию MOEX (/api/ta/chart-data/).
      // /crypto передаёт '/api/crypto/chart-data/' — тот же payload-контракт,
      // другой источник. Backward-compat: не задано → текущее поведение.
      dataUrlBase: opts.dataUrlBase || '/api/ta/chart-data/',
      // Live-price polling (MOEX ISS). Крипта/бэктест → opts.live=false (нет
      // /api/ta/live-price для крипты + сетапы исторические). Default — вкл.
      liveEnabled: opts.live !== false,
      // Строго realtime live-линия: цена/forming-close из Tinkoff (задержка
      // секунды), а не из бортового ISS-снимка (лаг ~15 мин у бесплатного фида).
      // /ml-setups передаёт true. Default — борт (совместимость всех страниц).
      liveRealtime: opts.liveRealtime === true,
      // Подписи делений шкалы времени по-русски («окт», а не «Oct»). Включает только кадр
      // «Лучшая сделка дня» для /about (static/about/chart_card.html, 04.10.2026); у страниц
      // сайта шкала прежняя — по умолчанию выкл.
      ruTickMarks: opts.ruTickMarks === true,
      // Логотип TradingView в углу графика (опция LWC layout.attributionLogo). Выключен на всех
      // графиках сайта (решение владельца 04.10.2026): лицензия библиотеки требует уведомление и
      // ссылку на TradingView на странице сайта — они на /privacy, раздел «Сторонние компоненты».
      // Прямые createChart без движка (invest.html, лаборатории) выключают его сами.
      attributionLogo: opts.attributionLogo === true,
      // Доп. query-параметры к chart-data URL (например '&min_er_lead=0.2' для
      // визуального A/B анти-пилы на /crypto). Меняется через setExtraQuery().
      extraQuery: opts.extraQuery || '',
      layers: {sr: true, fib: false, ema: true, mtf: true},
      // RSI pane visibility (привязан к фильтру DIVERGENCE на /setups).
      // По умолчанию true для /ta и других стандартных потребителей.
      showRsi: opts.showRsi !== false,
      // Trigger-type filter (whitelist DB-format strings, например 'STOP_HUNT_MTF').
      // null = показывать все типы (default для /ta и других стандартных потребителей).
      // На /setups передаётся Set с типами из STATE.filters.trigger_types (UI→DB
      // через _UI_TO_DB_TT). Фильтр применяется КЛИЕНТСКИ к state.triggersData
      // → setMarkers — не требует обращения к серверу, кэш chart-data не
      // invalidate'ится при смене чекбоксов.
      triggerTypes: null,
      // Цена ключевого уровня выбранного сетапа (STOP_HUNT/FB/Климакс) — совпадающая
      // MTF-подпись подсвечивается (ярче+жирнее), чтобы выделить уровень сетапа среди
      // прочих, НЕ рисуя дубль-линию. null = без подсветки. Ставится setHighlightLevel.
      highlightLevel: null,
    };

    // Применяет state.triggerTypes к state.triggersData → setMarkers.
    // Вызывается из renderChart (initial) и из setTriggerTypes (UI change).
    // Видим ли trigger как маркер: whitelist state.triggerTypes (UI-чекбоксы
    // /setups). DIVERGENCE показывается стрелкой направления входа (2026-05-31,
    // после selection rework + walk-forward валидации); клик по нему рисует
    // divergence-линии. Единый предикат для маркеров (_applyTriggerFilter) и
    // hover-tooltip — чтобы tooltip показывался ровно для ВИДИМЫХ маркеров.
    function _isTriggerVisible(t) {
      // Solo-режим: видим ТОЛЬКО выбранный сетап (и для маркеров, и для tooltip).
      if (state.soloKey) {
        var sbd = String(state.soloKey.bar_date || '').substring(0, 16);
        return String(t.bar_date || '').substring(0, 16) === sbd
            && t.trigger === state.soloKey.trigger
            && (state.soloKey.direction == null || t.direction === state.soloKey.direction)
            // ТФ: на одном баре бывают 1ч и 4ч сетапы одного типа и направления
            // (метки баров совпадают) — без него solo брал чужой (20.08, DOMRF).
            && (state.soloKey.tf == null || String(t.tf || '1h') === state.soloKey.tf);
      }
      // DE_* (3 admin visual-test версии) — SOLO-ONLY: не показываем как multi-маркеры.
      // Стрелка + зигзаг DE-версии рисуются только по клику на строку/маркер (solo),
      // 1-в-1 как линии дивергенции — иначе «все включённые типы сразу» + дубли
      // геометрии (bear_flag и double_bottom одной фигуры делят _viz) = каша.
      // Prod DOUBLE_EXTREME_MTF (амбер) — обычный триггер, его НЕ трогаем.
      if (t && typeof t.trigger === 'string' && t.trigger.indexOf('DE_') === 0) return false;
      if (state.triggerTypes && !state.triggerTypes.has(t.trigger)) return false;
      return true;
    }

    // Свеча графика, внутри которой лежит ТОЧНЫЙ момент выхода at (unix): индекс последней
    // свечи с time ≤ at. undefined — момента нет, свечи не числовые или момент раньше первой
    // свечи (действует правило бара выхода); null — момент за концом загруженных свечей
    // (рисовать не на чем). Правило «выход в свече своего момента» живёт только здесь: его
    // берут solo-выход (exitMomentTime, с проверкой цены) и квадраты обычного вида. Зачем
    // (14.09): модель «стоп идёт за ценой» знает мелкую свечу выхода, а по бару выхода линия
    // на часовом графике вставала на следующий час (Forward 44 из 199) или на первый час
    // 4ч-блока (витрина у админа — 323 из 8 237, раньше выхода).
    function _momentCandleIndex(cs, at) {
      if (typeof at !== 'number' || !cs || !cs.length || typeof cs[0].time !== 'number' || at < cs[0].time) return undefined;
      var n = cs.length, d = [];
      for (var i = 1; i < n && i < 40; i++) d.push(cs[i].time - cs[i - 1].time);
      d.sort(function (a, b) { return a - b; });
      var step = d.length ? d[Math.floor(d.length / 2)] : 0;
      if (step && at >= cs[n - 1].time + step) return null;
      var lo = 0, hi = n - 1;
      while (lo < hi) { var mid = (lo + hi + 1) >> 1; if (cs[mid].time <= at) lo = mid; else hi = mid - 1; }
      // Момент после конца найденной свечи — свечи его часа на графике нет (утренний гэп 06:50–06:59 при
      // часовых свечах с 07:00, пропуск у неликвида): это первая свеча ПОСЛЕ момента, а не последняя до
      // него. Иначе гэп понедельника вставал на пятницу, а со свечами выходных — на воскресенье (28.09.2026).
      if (step && at >= cs[lo].time + step && lo + 1 < n) return lo + 1;
      return lo;
    }

    function _clearSoloSeries() {
      (state.soloSeries || []).forEach(function(s) {
        try { state.chart.removeSeries(s); } catch (e) {}
      });
      state.soloSeries = [];
    }

    // Solo-режим (/setups): показываем ТОЛЬКО выбранный сетап — вход на entry_price,
    // выход на exit_price, точно по цене. Каждый маркер — на своей прозрачной
    // line-серии (маркер inBar на line-серии садится РОВНО на значение/цену точки,
    // в отличие от bar-anchored маркера свечной серии). Match по (bar_date[:16],
    // trigger, direction) — формат bar_date у /setups «YYYY-MM-DD HH:MM».
    // ТФ → длительность бара, сек (D1 — календарные сутки).
    function _tfSec(tf) {
      return tf === 'D1' ? 86400 : tf === '4h' ? 14400 : tf === '15m' ? 900 : tf === '5m' ? 300 : 3600;
    }
    function _renderSoloMarkers() {
      _clearSoloSeries();
      var sel = state.soloKey;
      var bd = String(sel.bar_date || '').substring(0, 16);
      function match(x) {
        return String(x.bar_date || '').substring(0, 16) === bd
            && x.trigger === sel.trigger
            && (sel.direction == null || x.direction === sel.direction)
            && (sel.tf == null || String(x.tf || '1h') === sel.tf);
      }
      var entry = state.triggersData.filter(match)[0];
      var exit = (state.exitMarkers || []).filter(match)[0];
      var cs = (state.lastData && state.lastData.candles) || [];
      // Привязка маркера к РЕАЛЬНОЙ свече. line-серия требует time, совпадающий с
      // баром свечной серии — иначе на разреженных 15m/5m (у неликвида H1-время может
      // не иметь свечи) фантомный бар ломал шкалу → ПУСТОЙ график.
      function nearestTime(t) {
        if (!cs.length || t == null || typeof t !== 'number' || typeof cs[0].time !== 'number') return t;
        var bt = cs[0].time, bdst = Math.abs(cs[0].time - t);
        for (var i = 1; i < cs.length; i++) { var d = Math.abs(cs[i].time - t); if (d < bdst) { bdst = d; bt = cs[i].time; } }
        return bt;
      }
      // Выход: свеча, ЧЕЙ ДИАПАЗОН [low,high] содержит exit_price, ближайшая к exit_bar.
      // trail-выход записывается на бар ПОЗЖЕ пересечения уровня (+ цена «гэпает» над
      // уровнем между барами) → exit_price бывает вне диапазона записанного бара.
      // Ставим квадрат на тот бар, где цена реально была у уровня выхода.
      // tLo/tHi — окно поиска (вход … бар выхода): выход не бывает раньше входа и не
      // позже записанного бара. Без окна ближайшая «похожая» цена находилась в чужом
      // дне — HYDR 10.09 (выход на фьючерсе в ценах акции, у акции такой цены после
      // входа не было): линия легла на 08.09, до входа (репорт 14.09.2026).
      function containingTime(price, t, tLo, tHi) {
        if (!cs.length || t == null || price == null || typeof t !== 'number' || typeof cs[0].time !== 'number') return nearestTime(t);
        var bt = null, bdst = Infinity;
        for (var i = 0; i < cs.length; i++) {
          var c = cs[i];
          if ((tLo != null && c.time < tLo) || (tHi != null && c.time > tHi)) continue;
          if (c.low != null && c.high != null && price >= c.low - 1e-9 && price <= c.high + 1e-9) {
            var d = Math.abs(c.time - t); if (d < bdst) { bdst = d; bt = c.time; }
          }
        }
        return bt != null ? bt : nearestTime(t);
      }
      // Solo-выход по точному моменту: свеча момента (_momentCandleIndex), если цена выхода в её
      // диапазоне и свеча не раньше входа; цена вне свечи → поиск containingTime в окне
      // «вход … конец этой свечи». undefined / null — как у _momentCandleIndex.
      function exitMomentTime(price, at, tLo) {
        var i = _momentCandleIndex(cs, at);
        if (i == null) return i;
        var c = cs[i];
        if ((tLo == null || c.time >= tLo) && c.low != null && c.high != null
            && price >= c.low - 1e-9 && price <= c.high + 1e-9) return c.time;
        return containingTime(price, c.time, tLo, i + 1 < cs.length ? cs[i + 1].time - 1 : null);
      }
      // Guard + try/catch: маркер с битым time/price (или редкий LWC-сбой на line-серии)
      // НЕ должен ронять рендер всего графика (был баг с пустыми графиками + 15m/5m).
      function addPt(time, price, shape, color, size) {
        if (time == null || price == null || !isFinite(price)) return;
        try {
          var s = state.chart.addLineSeries({color: 'rgba(0,0,0,0)', lineWidth: 1,
            lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false});
          s.setData([{time: time, value: price}]);
          s.setMarkers([{time: time, position: 'inBar', shape: shape,
                         color: color, text: '', size: size}]);
          state.soloSeries.push(s);
        } catch (e) { /* solo-маркер best-effort, график важнее */ }
      }
      // /ml-setups: первая свеча строго ПОСЛЕ сигнального бара = бар входа (вход на
      // её open; robust к разрывам сессий). Fallback — сигнал последний бар.
      function firstAfter(t) {
        if (t == null) return t;
        for (var i = 0; i < cs.length; i++) { if (cs[i].time > t) return cs[i].time; }
        return t;
      }
      // Свеча, СОДЕРЖАЩАЯ момент t (последняя с time <= t) — когда время входа задано
      // явно (журнал sim-трейда: вход по клику, может быть на много баров позже
      // сигнала, поэтому firstAfter(сигнал) там промахнулся бы).
      function containingBar(t) {
        if (t == null || !cs.length || typeof cs[0].time !== 'number') return nearestTime(t);
        var bt = null;
        for (var i = 0; i < cs.length; i++) { if (cs[i].time <= t) bt = cs[i].time; else break; }
        return bt != null ? bt : nearestTime(t);
      }
      // Последняя свеча, начавшаяся СТРОГО ДО t. Нужна, чтобы стрелка детекции стояла
      // на ЗАКРЫТИИ сигнального часа, а не в его начале: сигнал H1 физически не
      // существует до конца бара, а маркер в начале часа создавал впечатление, что
      // вход опоздал на час (на 5m это 12 свечей движения). На H1 вернёт сам бар.
      function lastBarBefore(t) {
        if (t == null || !cs.length || typeof cs[0].time !== 'number') return nearestTime(t);
        var bt = null;
        for (var i = 0; i < cs.length; i++) { if (cs[i].time < t) bt = cs[i].time; else break; }
        return bt != null ? bt : nearestTime(t);
      }
      // Шаг свечной сетки (медиана разниц соседних времён) — нужен, чтобы отличить
      // ТФ мельче сигнального часа от D1, где весь час лежит внутри одного бара.
      function barStep() {
        if (cs.length < 3 || typeof cs[0].time !== 'number') return 0;
        var d = [];
        for (var i = 1; i < cs.length && i < 40; i++) d.push(cs[i].time - cs[i - 1].time);
        d.sort(function (a, b) { return a - b; });
        return d[Math.floor(d.length / 2)] || 0;
      }
      // Бар market-входа. На ТФ мельче часа — первая свеча с открытия следующего часа.
      // На D1 (бар ДЛИННЕЕ часа) так нельзя: «первая свеча после 09:00» перескакивает
      // на СЛЕДУЮЩИЙ день (кейс IVAT: вход рисовался 10.08 вместо 7.08, через выходные).
      // Там и сигнал, и вход лежат внутри одного дневного бара → берём содержащий.
      function marketEntryBar(anchor) {
        return barStep() > 3600 ? containingBar(anchor) : firstAtOrAfter(anchor);
      }
      // Первая свеча с time >= anchor. Для market-входа бар известен ТОЧНО (открытие
      // следующего часа) — искать его по цене не нужно и опасно: та же цена могла
      // встретиться раньше внутри сигнального часа, и маркер вставал не на ту свечу.
      function firstAtOrAfter(t) {
        if (t == null || !cs.length || typeof cs[0].time !== 'number') return nearestTime(t);
        for (var i = 0; i < cs.length; i++) { if (cs[i].time >= t) return cs[i].time; }
        return cs[cs.length - 1].time;
      }
      // Бар ВХОДА: первая свеча начиная с anchor, чей диапазон [low,high] содержит
      // цену входа. ⚠ 2026-08-12: раньше вход ставился по ВРЕМЕНИ (containingBar /
      // firstAfter), и на младшем ТФ линия висела ЗА пределами свечи — такой цены
      // в том 5-минутном баре не существовало. Цена входа относится к ЧАСОВОМУ
      // сигналу: у лимитки (двойная вершина/дно) она достигается где-то внутри
      // часа пробоя, у market-входа это open первого бара следующего часа. Ищем
      // бар, где цена реально была, — как давно делает containingTime для выхода.
      // Fallback: ближайший по времени (лучше приблизительно, чем не показать).
      // Бар входа для стрелки solo (/setups). На ТФ сигнала (и старше) — сам сигнальный
      // бар, как раньше. На МЛАДШЕМ ТФ (5m/15m под часовым или четырёхчасовым сигналом)
      // — бар, где цена входа реально была достигнута: у лимитки (entry_intrabar —
      // «скрытый уровень», двойные вершина/дно) первая свеча от начала сигнального бара,
      // чей диапазон содержит цену; у market-входа — открытие первого бара после
      // сигнального. Раньше стрелка вставала на первую пятиминутку сигнального часа по
      // прокси-цене — ROLO 03.09: лимитка 0.3752 исполнилась в 17:30, стрелка стояла в
      // 17:00 по 0.3768 (репорт владельца).
      function soloEntryBar(e) {
        var sig = _tfSec(e.tf || (sel && sel.tf) || '1h'), cur = barStep();
        if (e.time == null || !cs.length || !cur || cur >= sig) return nearestTime(e.time);
        return e.entry_intrabar ? entryBarTime(e.entry_price, e.time)
                                : marketEntryBar(e.time + sig);
      }
      function entryBarTime(price, anchor) {
        if (!cs.length || price == null || anchor == null) return containingBar(anchor);
        if (typeof cs[0].time !== 'number') return containingBar(anchor);
        for (var i = 0; i < cs.length; i++) {
          var c = cs[i];
          if (c.time < anchor) continue;
          if (c.low != null && c.high != null &&
              price >= c.low - 1e-9 && price <= c.high + 1e-9) return c.time;
        }
        return containingTime(price, anchor);
      }
      // Короткая горизонтальная линия на уровне price, центрированная на баре
      // centerTime (~2 соседние свечи). Только СУЩЕСТВУЮЩИЕ времена свечей → не
      // добавляет фантомных слотов в шкалу (иначе сдвиг всего графика).
      function addShortLine(centerTime, price, color, width) {
        if (centerTime == null || price == null || !isFinite(price) || !cs.length) return;
        if (typeof cs[0].time !== 'number') return;
        var ci = 0, cd = Math.abs(cs[0].time - centerTime);
        for (var i = 1; i < cs.length; i++) { var d = Math.abs(cs[i].time - centerTime); if (d < cd) { cd = d; ci = i; } }
        var pts = [];
        if (ci - 1 >= 0) pts.push({time: cs[ci - 1].time, value: price});
        pts.push({time: cs[ci].time, value: price});
        if (ci + 1 < cs.length) pts.push({time: cs[ci + 1].time, value: price});
        if (pts.length < 2) return;
        try {
          var s = state.chart.addLineSeries({color: color, lineWidth: width,
            lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false});
          s.setData(pts);
          state.soloSeries.push(s);
        } catch (e) { /* best-effort */ }
      }
      // Маркеры свечной серии: extraMarker + опц. стрелка входа над/под баром сигнала
      // (/ml-setups: entry._entryAboveBelow). /setups флаг не ставит → как было.
      var candleMarkers = state.extraMarker ? [state.extraMarker] : [];
      _divMarkers().forEach(function(m) { candleMarkers.push(m); });
      if (entry && entry._entryAboveBelow && entry.entry_price != null) {
        // Стрелка — на ЗАКРЫТИИ сигнального бара, но ТОЛЬКО для market-входа: там
        // сигнал становится известен лишь по закрытию часа. У ЛИМИТКИ (двойные
        // вершина/дно) наоборот — фигура и уровень шеи известны заранее, ордер стоит
        // до пробоя и исполняется ВНУТРИ сигнального часа. Перенос стрелки на конец
        // часа ставил её ПРАВЕЕ входа, чего быть не может (кейс HNFG 12.08 на 5m).
        var _sigSec = entry._signalBarSec || 3600;
        var _arrowT = entry._entryAtOpen ? lastBarBefore(entry.time + _sigSec)
                                         : nearestTime(entry.time);
        candleMarkers.push({time: _arrowT,
          position: entry.direction === 'LONG' ? 'belowBar' : 'aboveBar',
          shape: entry.shape, color: entry.color, text: '', size: 2});
      }
      state.candleSeries.setMarkers(candleMarkers);
      try { window.__taSoloDebug = entry ? {time: soloEntryBar(entry), price: entry.entry_price, tf: entry.tf, intrabar: !!entry.entry_intrabar, chartTf: state.currentTf} : null; } catch (e) {}
      // Вход: /ml-setups — короткая линия на баре входа (open след. бара); /setups —
      // стрелка точно на цене входа (addPt inBar).
      if (entry && entry.entry_price != null) {
        // Якорь = момент, С КОТОРОГО ищем бар входа: явный _entryMarkTime (лимитка —
        // сам сигнальный бар, цена достигается внутри него) либо первый бар после
        // сигнала (market-вход по open следующего). Дальше entryBarTime находит
        // свечу, в диапазоне которой цена реально была.
        if (entry._entryMark) {
          var _anc = entry._entryMarkTime != null ? entry._entryMarkTime : firstAfter(entry.time);
          // market-вход (_entryAtOpen): бар известен точно — открытие следующего часа.
          // Лимитка: цена достигается где-то внутри часа → ищем бар по цене.
          var _eb = entry._entryAtOpen ? marketEntryBar(_anc)
                                       : entryBarTime(entry.entry_price, _anc);
          entry._soloEntryTime = _eb;
          try { if (window.__taSoloDebug) window.__taSoloDebug.entryLine = {time: _eb, price: entry.entry_price}; } catch (e) {}
          addShortLine(_eb, entry.entry_price, entry.color, 2);
        }
        else if (!entry._entryAboveBelow) {
          var _seb = soloEntryBar(entry);
          // Бар стрелки запоминаем на самом триге: тултип по наведению ищет сетап по
          // времени СИГНАЛЬНОГО бара, а стрелка solo стоит на баре ВХОДА (на 5м под
          // 4ч-сигналом — четыре часа правее). Без этого наведение на стрелку молчало.
          entry._soloEntryTime = _seb;
          addPt(_seb, entry.entry_price, entry.shape, entry.color, 2);
        }
      }
      // Выход: /ml-setups — короткая линия; /setups — квадрат. Правило одно для всех страниц:
      //  • страница знает ТОЧНЫЙ момент выхода (exit.exit_at — мелкая свеча модели «стоп идёт
      //    за ценой») — выход в свече графика, внутри которой лежит момент (exitMomentTime);
      //  • знает только бар выхода (часовой расчёт) — поиск между входом и концом этого бара
      //    (у сделки 4ч на мелком графике это ещё четыре часа после его начала); нет в окне
      //    свечи с такой ценой — ближайшая по времени.
      if (exit && exit.exit_price != null) {
        var _exLo = entry ? (entry._soloEntryTime != null ? entry._soloEntryTime : entry.time) : null;
        var _exT = exitMomentTime(exit.exit_price, exit.exit_at, _exLo);
        if (_exT === undefined) {
          var _exHi = (typeof exit.time === 'number')
            ? exit.time + ((entry && entry._signalBarSec) || 3600) - 1 : null;
          _exT = containingTime(exit.exit_price, exit.time, _exLo, _exHi);
        }
        exit._drawnTime = _exT;   // тултип отвечает и на свече, где линия нарисована
        if (_exT != null) {
          if (exit._exitLine) addShortLine(_exT, exit.exit_price, exit.color, 2);
          else addPt(_exT, exit.exit_price, 'square', exit.color, 1);
        }
        try { if (window.__taSoloDebug) window.__taSoloDebug.exit = {time: _exT, price: exit.exit_price, at: (exit.exit_at != null ? exit.exit_at : null)}; } catch (e) {}
      }
      // Double-extreme зигзаг и в solo (по умолчанию показан для выбранной фигуры).
      _syncDoubleExtremeZigzags();
    }

    // Маркер дивидендной отсечки (общий для обычного вида и solo).
    function _divMarkers() {
      return (state.dividendMarks || []).map(function(d) {
        return {time: d.time, position: 'aboveBar', shape: 'circle',
                color: '#d4a016', text: 'Отсечка', size: 1};
      });
    }

    function _applyTriggerFilter() {
      if (!state.candleSeries) return;
      if (state.soloKey) { _renderSoloMarkers(); return; }
      _clearSoloSeries();   // вышли из solo → убрать price-anchored серии
      var markers = state.triggersData
        .filter(_isTriggerVisible)
        .map(function(t) {
          // DIVERGENCE: стрелка направления входа (arrowUp+belowBar для LONG /
          // arrowDown+aboveBar для SHORT). АКТИВНАЯ (та, к которой сейчас показаны
          // линии = selectedDivergenceKey) — ярко-белая, остальные — чуть бледнее
          // (2026-06-05). Если ничего не выбрано — все белые.
          var col = t.color;
          if (t.trigger === 'DIVERGENCE') {
            col = (state.selectedDivergenceKey == null
                   || t.bar_date === state.selectedDivergenceKey)
              ? '#ffffff'
              : 'rgba(255,255,255,0.4)';
          } else if (t.trigger === 'DOUBLE_EXTREME_MTF') {
            // янтарный; зигзаги показаны по умолчанию. Скрытые кликом — бледнее.
            col = state.deHiddenKeys[_deKey(t)] ? 'rgba(255,193,7,0.45)' : '#ffc107';
          } else if (_isDeType(t)) {
            // DE_* (3 admin-версии) — цвет из объекта (коралл/циан/маджента);
            // видимость по чекбоксам сайдбара, скрытый кликом зигзаг → маркер бледнее.
            col = state.deHiddenKeys[_deKey(t)] ? _dimHex(t.color) : t.color;
          }
          return {time: t.time, position: t.position, shape: t.shape,
                  color: col, text: '', size: 2};
        });
      // Q2: маркеры выхода (квадраты, красный=стоп/убыток, зелёный=тейк/трейл) —
      // тот же фильтр видимости по типу триггера, что и входы.
      var _csAll = (state.lastData && state.lastData.candles) || [];
      (state.exitMarkers || []).filter(_isTriggerVisible).forEach(function(m) {
        // точный момент выхода (exit_at) → свеча, внутри которой он лежит; нет — бар выхода
        var _i = _momentCandleIndex(_csAll, m.exit_at);
        if (_i === null) return;            // момент за концом загруженных свечей
        var _t = (_i === undefined) ? m.time : _csAll[_i].time;
        m._drawnTime = _t;                  // тултип отвечает и на свече, где квадрат нарисован
        markers.push({time: _t, position: m.position || 'inBar',
                      shape: 'square', color: m.color, text: '', size: 1});
      });
      if (state.extraMarker) markers.push(state.extraMarker);
      _divMarkers().forEach(function(m) { markers.push(m); });
      markers.sort(function(a, b) { return a.time - b.time; });
      state.candleSeries.setMarkers(markers);
      // Double-extreme зигзаги: показаны по умолчанию для всех видимых фигур
      // (кроме скрытых кликом). Синхронизируем с текущей видимостью маркеров.
      _syncDoubleExtremeZigzags();
    }

    // Рисует MTF-линии (D1+W1+density) с учётом подсветки. Отдельный массив state._mtfLines
    // (не priceLinesArr) → перерисовываем при смене подсветки без трогания S/R/Fib.
    // opacity 0.8 (видны на фоне свечей; было 0.65 — терялись). Все линии 1px (LWC min,
    // 0.5px невозможно); подсвеченный уровень сетапа выделяем только ЦВЕТОМ — ярче (#ffd24a)
    // + full opacity, толщину НЕ меняем (по фидбэку «не делать толще»).
    function _drawMtfLines() {
      if (state._mtfLines && state.candleSeries) {
        state._mtfLines.forEach(function(pl) { try { state.candleSeries.removePriceLine(pl); } catch(e) {} });
      }
      state._mtfLines = [];
      if (!state.candleSeries || !state.layers.mtf || !state.lastData || !state.lastData.levels) return;
      (state.lastData.levels.mtf || []).forEach(function(lv) {
        var isHi = state.highlightLevel != null
          && Math.abs(lv.price - state.highlightLevel) <= Math.max(1e-6, Math.abs(state.highlightLevel) * 0.0015);
        state._mtfLines.push(state.candleSeries.createPriceLine({
          price: lv.price,
          color: isHi ? 'rgba(255,210,74,1)' : 'rgba(212,160,22,0.8)',
          lineWidth: 1, lineStyle: 2,
          title: '', axisLabelVisible: false,
        }));
      });
    }

    // ── renderChart ──
    function renderChart(data) {
      if (state.chart) { state.chart.remove(); state.chart = null; }
      chartEl.innerHTML = '';
      state.triggersData = (data.triggers || []).slice();
      state.exitMarkers = (data.exit_markers || []).slice();   // Q2: точки выхода
      // Дивидендные отсечки: маркер на ПОСЛЕДНЕМ баре «с дивидендом» —
      // назавтра бумага открывается гэпом вниз примерно на его величину
      // (шорт платит дивиденд, лонг получает). Показываем всегда: это
      // свойство бумаги, а не сетапа, поэтому фильтр типов его не касается.
      state.dividendMarks = (data.dividend_marks || []).slice();
      // Reset stale references после chart.remove() — series мертвы, key должен
      // сбрасываться чтобы selectTrigger() с тем же bar_date не сработал как toggle off.
      state.divergenceSeries = [];
      state.soloSeries = [];   // refs мертвы после chart.remove() — пересоздадутся в _applyTriggerFilter
      state.selectedDivergenceKey = null;
      state.bearishFlagSeries = [];
      state.selectedFlagKey = null;
      // ⚠ 2026-08-12: deSeriesByKey тоже держит МЁРТВЫЕ ссылки после chart.remove().
      // Без сброса геометрия фигуры (зигзаг двойной вершины/дна, флаг) исчезала при
      // смене ТФ и не возвращалась даже кликом: _drawDoubleExtremeKey начинается с
      // idempotent-guard `if (state.deSeriesByKey[key]) return` — словарь непустой,
      // значит «уже нарисовано», хотя серий больше нет. Лечилось только F5.
      // removeSeries звать НЕ надо — чарт уже уничтожен вместе с сериями.
      state.deSeriesByKey = {};
      state.rsiSeries = null;
      try { if (state._rsiLevelRO) { state._rsiLevelRO.disconnect(); state._rsiLevelRO = null; } } catch (e) {}

      state.chart = LightweightCharts.createChart(chartEl, {
        // autoSize: ResizeObserver на контейнере. Без него LWC создаёт canvas'ы
        // с дефолтным буфером 300×150, если в момент create() контейнер ещё
        // нулевого размера (как происходит при init /fa правой панели до того,
        // как layout успел применить flex-grow). Тогда CSS-размеры canvas'ов
        // становятся правильными (742×468), но pixel buffer остаётся 300×150 —
        // chart рендерится «растянутым» и invisible детали (включая
        // тонкую линию текущей цены) теряются. autoSize реагирует на любые
        // изменения контейнера в течение жизни графика.
        autoSize: true,
        layout: {background: {type: 'solid', color: '#0c1116'}, textColor: '#8a939e',
                 attributionLogo: state.attributionLogo},
        grid: {vertLines: {color: '#1e2d3d', style: 1}, horzLines: {color: '#1e2d3d', style: 1}},
        crosshair: {mode: 0},
        rightPriceScale: {borderColor: '#1e2d3d'},
        timeScale: _timeScaleOpts(),
        // Подпись времени под курсором. Четырёхчасовые бары нарезаны блоками от
        // полуночи МСК, а торги MOEX открываются в 07:00 — поэтому у блока
        // 04:00–08:00 подпись «04:00» читается как «биржа закрыта» (репорт
        // владельца 24.08). Печатаем первый ТОРГОВЫЙ час блока, как в таблице и
        // на карточках. ⚠ Сдвигается ТОЛЬКО подпись: время бара не меняется,
        // поэтому маркеры, solo-матч и клик по бару работают по-прежнему.
        localization: {timeFormatter: _fmtAxisTime},
        handleScroll: true, handleScale: true,
      });

      // Шкала времени. ruTickMarks — деления по-русски тем же сдвигом 4ч-блока, что у подписи
      // под курсором (_fmtAxisTime): «окт», «2», «12:00». Без флага — как было (деления LWC).
      function _timeScaleOpts() {
        var ts = {borderColor: '#1e2d3d', timeVisible: state.currentTf !== 'D1', secondsVisible: false};
        if (state.ruTickMarks) ts.tickMarkFormatter = _ruTickMark;
        return ts;
      }
      function _ruTickMark(t, type) {
        if (typeof t !== 'number') return null;          // BusinessDay — подпись LWC
        var d = new Date(t * 1000);
        if (isNaN(d.getTime())) return null;
        if (type === 0) return String(d.getUTCFullYear());
        if (type === 1) { var m = _MON_RU[d.getUTCMonth()]; return m === 'мая' ? 'май' : m; }
        if (type === 2) return String(d.getUTCDate());
        if (state.dataUrlBase === '/api/ta/chart-data/' && state.currentTf === '4h'
            && d.getUTCHours() < MSK_SESSION_OPEN_H) {
          d = new Date(d.getTime() + (MSK_SESSION_OPEN_H - d.getUTCHours()) * 3600 * 1000);
        }
        return ('0' + d.getUTCHours()).slice(-2) + ':' + ('0' + d.getUTCMinutes()).slice(-2);
      }

      // ── подпись времени на оси ──────────────────────────────
      // Воспроизводит вид LWC («24 авг. '26 04:00»), но у 4ч-блоков раньше
      // открытия сессии печатает первый торговый час. Только для MOEX: у крипты
      // торги круглосуточные, там блок 04:00 полноценный (dataUrlBase свой).
      function _fmtAxisTime(t) {
        if (typeof t !== 'number') return String(t);   // BusinessDay-объект → как есть
        var d = new Date(t * 1000);
        if (isNaN(d.getTime())) return String(t);
        var isMoex = state.dataUrlBase === '/api/ta/chart-data/';
        if (isMoex && state.currentTf === '4h' && d.getUTCHours() < MSK_SESSION_OPEN_H) {
          d = new Date(d.getTime() + (MSK_SESSION_OPEN_H - d.getUTCHours()) * 3600 * 1000);
        }
        var out = d.getUTCDate() + ' ' + _MON_RU[d.getUTCMonth()] + ". '"
                + String(d.getUTCFullYear()).slice(2);
        if (state.currentTf !== 'D1') {
          out += ' ' + ('0' + d.getUTCHours()).slice(-2) + ':' + ('0' + d.getUTCMinutes()).slice(-2);
        }
        return out;
      }

      // Precision: MOEX рубли → 2/0.01 (default, для консистентности шкалы:
      // LWC иначе auto-detect'ит precision=0 для целых цен типа PLZL). Крипта
      // отдаёт per-symbol precision в meta (BTC 2, XLM/DOGE 4-6) — иначе
      // minMove=0.01 квантует sub-dollar монеты в «лесенку». Backward-compat:
      // нет meta.price_precision → старое поведение 2/0.01.
      var _pp = (data.meta && data.meta.price_precision != null) ? data.meta.price_precision : 2;
      var _pm = (data.meta && data.meta.price_min_move != null) ? data.meta.price_min_move : 0.01;
      state.candleSeries = state.chart.addCandlestickSeries({
        upColor: '#3fb84f', downColor: '#f75049',
        borderUpColor: '#3fb84f', borderDownColor: '#f75049',
        wickUpColor: '#3fb84f', wickDownColor: '#f75049',
        lastValueVisible: false, priceLineVisible: false,
        priceFormat: {type: 'price', precision: _pp, minMove: _pm},
      });
      var allCandles = data.candles.slice();
      state.candleSeries.setData(allCandles);

      // Trigger markers — все они с непустым matches (бэк фильтрует).
      // DIVERGENCE-маркеры скрыты по решению UX: пользователь сам определяет
      // момент входа, наша задача — детектировать факт дивергенции. Сам
      // trigger остаётся в triggersData (selectTrigger() для linrew работает).
      // Trigger-type filter (state.triggerTypes) применяется внутри
      // _applyTriggerFilter() — позволяет /setups скрывать VSA/FB и т.д.
      // через UI-чекбоксы без перезагрузки chart-data.
      if (state.triggersData.length > 0) {
        _applyTriggerFilter();
      }

      // EMA
      if (state.layers.ema) {
        if (data.ema && data.ema.ema50) {
          state.ema50Series = state.chart.addLineSeries({
            color: '#d4a016', lineWidth: 1, priceLineVisible: false,
            lastValueVisible: false, crosshairMarkerVisible: false,
          });
          state.ema50Series.setData(data.ema.ema50);
        }
        if (data.ema && data.ema.ema200) {
          state.ema200Series = state.chart.addLineSeries({
            color: '#ef873d', lineWidth: 1, priceLineVisible: false,
            lastValueVisible: false, crosshairMarkerVisible: false,
          });
          state.ema200Series.setData(data.ema.ema200);
        }
      }

      // Volume — нижний pane. Высота зависит от того, показывается ли RSI:
      //   с RSI:    volume 12% (top:0.88), price 60% (margins 0.05/0.35), rsi 18% (0.67/0.15)
      //   без RSI:  volume 15% (top:0.85), price 80% (margins 0.05/0.18)
      state.volumeSeries = state.chart.addHistogramSeries({
        priceFormat: {type: 'volume'}, priceScaleId: 'vol',
        priceLineVisible: false, lastValueVisible: false
      });
      var _useRsi = state.showRsi && data.rsi && data.rsi.length > 0;
      if (_useRsi) {
        state.chart.priceScale('vol').applyOptions({scaleMargins: {top: 0.88, bottom: 0}});
      } else {
        state.chart.priceScale('vol').applyOptions({scaleMargins: {top: 0.85, bottom: 0}});
        state.chart.priceScale('right').applyOptions({scaleMargins: {top: 0.05, bottom: 0.18}});
      }
      state.volumeSeries.setData(data.volume);

      // RSI pane — отдельная priceScale 'rsi', занимает 18% между price и volume.
      // Включается только если state.showRsi=true И в payload есть rsi data.
      if (_useRsi) {
        state.rsiSeries = state.chart.addLineSeries({
          priceScaleId: 'rsi',
          color: '#b385ff', lineWidth: 1,
          priceFormat: {type: 'price', precision: 0, minMove: 1},
          lastValueVisible: false, priceLineVisible: false,
          crosshairMarkerVisible: false,
          // Шкала ЖЁСТКО 0-100 — иначе RSI автомасштабируется по данным и искажает
          // уровни/размах (линия дивергенции «съезжает» визуально). Как в lab.
          autoscaleInfoProvider: function () { return {priceRange: {minValue: 0, maxValue: 100}}; },
        });
        state.chart.priceScale('rsi').applyOptions({
          scaleMargins: {top: 0.67, bottom: 0.15},
          borderColor: '#1e2d3d',
        });
        // Сжимаем price scale чтобы RSI и volume помещались внизу
        state.chart.priceScale('right').applyOptions({
          scaleMargins: {top: 0.05, bottom: 0.35},
        });
        state.rsiSeries.setData(data.rsi);
        // Уровни RSI: 70/30 — фиолетовые СПЛОШНЫЕ (как линия RSI #b385ff), 50 — серый
        // ПУНКТИР (2026-06-05 по запросу). Числа — HTML-оверлеи СПРАВА в поле графика
        // (НЕ на ценовой шкале: axisLabelVisible:false, title нет — иначе дублировались).
        var _rsiLevels = [
          {v: 70, line: 'rgba(179,133,255,0.5)', style: 0, num: '#b385ff'},
          {v: 50, line: 'rgba(138,147,158,0.3)', style: 2, num: 'rgba(138,147,158,0.7)'},
          {v: 30, line: 'rgba(179,133,255,0.5)', style: 0, num: '#b385ff'},
        ];
        _rsiLevels.forEach(function(lv) {
          state.rsiSeries.createPriceLine({
            price: lv.v, color: lv.line, lineWidth: 1, lineStyle: lv.style,
            axisLabelVisible: false,
          });
        });

        // HTML-оверлеи (LWC v4 не имеет pane-titles): "RSI(14)" слева + числа 70/50/30.
        chartEl.querySelectorAll('.rsi-pane-label, .rsi-level-label').forEach(function(el){ el.remove(); });
        if (getComputedStyle(chartEl).position === 'static') {
          chartEl.style.position = 'relative';
        }
        var rsiLabel = document.createElement('div');
        rsiLabel.className = 'rsi-pane-label';
        rsiLabel.textContent = 'RSI(14)';
        rsiLabel.style.cssText =
          'position:absolute;top:calc(67% + 4px);left:8px;'
          + 'color:#8a939e;font-size:10px;font-family:\"JetBrains Mono\",monospace;'
          + 'font-weight:600;letter-spacing:0.05em;text-transform:uppercase;'
          + 'pointer-events:none;z-index:5;'
          + 'text-shadow:0 0 4px rgba(12,17,22,0.9);';
        chartEl.appendChild(rsiLabel);
        _rsiLevels.forEach(function(lv) {
          var el = document.createElement('div');
          el.className = 'rsi-level-label';
          el.setAttribute('data-v', lv.v);
          el.textContent = lv.v;
          el.style.cssText =
            'position:absolute;right:54px;top:0;opacity:0;'
            + 'color:' + lv.num + ';font-size:9px;font-family:\"JetBrains Mono\",monospace;'
            + 'font-weight:600;pointer-events:none;z-index:5;'
            + 'text-shadow:0 0 3px rgba(12,17,22,0.95);';
          chartEl.appendChild(el);
        });
        // Точное вертикальное выравнивание чисел ПО ЛИНИЯМ через priceToCoordinate
        // (RSI scale 0-100): % от высоты chartEl мазал, т.к. шкала цены занимает не всю
        // высоту (внизу ось времени). Пересчёт на resize (autoSize двигает координаты).
        var _layoutRsiLevels = function() {
          if (!state.rsiSeries) return;
          [70, 50, 30].forEach(function(v) {
            var el = chartEl.querySelector('.rsi-level-label[data-v="' + v + '"]');
            if (!el) return;
            var y = null;
            try { y = state.rsiSeries.priceToCoordinate(v); } catch (e) {}
            if (y == null) { el.style.opacity = '0'; return; }
            el.style.top = (y - 6) + 'px';   // -6: центрируем 9px текст по линии
            el.style.opacity = '1';
          });
        };
        requestAnimationFrame(_layoutRsiLevels);
        requestAnimationFrame(function(){ requestAnimationFrame(_layoutRsiLevels); });
        try {
          state._rsiLevelRO = new ResizeObserver(function() { _layoutRsiLevels(); });
          state._rsiLevelRO.observe(chartEl);
        } catch (e) {}
      }

      // Price Lines (S/R + Fib)
      state.priceLinesArr = [];
      if (data.levels) {
        if (state.layers.sr) {
          (data.levels.sr || []).forEach(function(lv) {
            if ((lv.touches || 0) <= 2) return;   // слабые пивоты (≤2 касаний) — шум, не рисуем
            var isS = lv.type === 'S';
            state.priceLinesArr.push(state.candleSeries.createPriceLine({
              price: lv.price,
              color: isS ? 'rgba(63,184,79,0.6)' : 'rgba(247,80,73,0.6)',
              lineWidth: 1, lineStyle: 2,
              title: '',
              // axisLabelVisible: false — LWC рендерит плашку на ценовой шкале,
              // загораживая деления. Текстовые метки S1/R1 рисуем HTML-оверлеем
              // на самих линиях (см. _renderSRLabels ниже).
              axisLabelVisible: false,
            }));
          });
        }
        if (state.layers.fib) {
          (data.levels.fib || []).forEach(function(f) {
            var isKey = (f.level === 0 || f.level === 100);
            var isExt = (f.level > 100);
            state.priceLinesArr.push(state.candleSeries.createPriceLine({
              price: f.price,
              color: isExt ? 'rgba(87,165,255,0.6)' : isKey ? 'rgba(160,170,185,0.8)' : 'rgba(160,170,185,0.5)',
              lineWidth: 1, lineStyle: isExt ? 2 : 1,
              title: f.label || '',
              axisLabelVisible: true, axisLabelColor: '#0c1116',
              axisLabelTextColor: isExt ? '#57a5ff' : '#8a96a5',
            }));
          });
        }
        // MTF-уровни (D1+W1+density) — отдельным массивом state._mtfLines (см. _drawMtfLines),
        // чтобы перерисовывать при смене подсветки без трогания S/R/Fib. Подпись — HTML-оверлеем
        // (_renderSRLabels, с hover-тултипом source). axisLabel = canvas, к нему тултип не повесить.
        _drawMtfLines();
      }

      // Visible range

      var totalCandles = allCandles.length;
      var visibleBars = 280;
      var startIdx = Math.max(0, totalCandles - visibleBars);
      if (allCandles.length > 0) {
        state.chart.timeScale().setVisibleRange({
          from: allCandles[startIdx].time, to: allCandles[totalCandles - 1].time,
        });
        // Сдвиг видимого окна: пустое пространство справа между последним баром и
        // ценовой шкалой, в барах. Дефолт при открытии графика (потребители, которые
        // зовут setVisibleBars/setVisibleDays после load(), задают свой padding сами).
        state.chart.timeScale().scrollToPosition(RIGHT_OFFSET_BARS, false);
      }

      // Subscribe click — открыть bottom sheet (callback от вызывающего)
      // + handle divergence visualization для DIVERGENCE триггеров.
      state.chart.subscribeClick(function(param) {
        if (!param || !param.time) {
          _clearDivergence();   // DE-зигзаги показаны по умолчанию — клик по пустому их не трогает
          return;
        }
        // Tolerance: ±1 бар (3600 сек для 1H, 86400 для D1) — пользователь
        // может промахнуться пальцем на маленький круг.
        var tol = state.currentTf === '4h' ? 14400
                : state.currentTf === '1h' ? 3600
                : state.currentTf === '15m' ? 900
                : state.currentTf === '5m' ? 300 : 86400;
        var hits = state.triggersData.filter(function(t) {
          return Math.abs(t.time - param.time) <= tol;
        });
        if (hits.length === 0) {
          _clearDivergence();
          return;
        }
        // Группируем по точному времени бара — приоритет тем, что строго на param.time
        var exact = hits.filter(function(t) { return t.time === param.time; });
        var picks = exact.length > 0 ? exact : hits;
        // Divergence: рисуем линии если в picks есть DIVERGENCE с pivot info.
        var divHit = picks.filter(function(t) {
          return t.trigger === 'DIVERGENCE' && t.divergence;
        })[0];
        // Double bottom/top: клик по маркеру тоглит его зигзаг. Все DE-типы (prod +
        // 3 версии) — каждый свой зигзаг (ключ тип+бар).
        var deHit = picks.filter(function(t) {
          return _isDeType(t) && t.double_extreme;
        })[0];
        var flagHit = picks.filter(function(t) {
          return t.trigger === 'BEARISH_FLAG' && t.bearish_flag;
        })[0];
        if (divHit) {
          _clearBearishFlag(); _drawDivergence(divHit);   // DE-зигзаги остаются (независимый слой)
        } else if (flagHit) {
          // Флаг показан по умолчанию (per-key, как зигзаги DE) → клик его прячет/
          // возвращает тем же общим toggle. Раньше был single-selection draw —
          // при авто-отрисовке это давало вторую копию поверх.
          _clearDivergence(); _clearBearishFlag(); _toggleDoubleExtreme(flagHit);
        } else if (deHit) {
          _clearDivergence(); _clearBearishFlag(); _toggleDoubleExtreme(deHit);   // DE-маркер → его зигзаг
        } else {
          _clearDivergence(); _clearBearishFlag();
        }
        if (state.onMarkerClick) state.onMarkerClick(picks);
      });

      if (state.liveEnabled) startLivePrice(data.meta.ticker);
      setupMarkerTooltip();
      _renderSRLabels();
    }

    // ── S/R text labels (HTML overlay, не на ценовой шкале) ──
    // ── Divergence visualization helpers ──
    // На click по DIVERGENCE-маркеру рисуем 2 линии (price + RSI pane):
    //   - на price scale: prev_pivot_date → cur_pivot_date
    //   - на RSI scale: prev_rsi_date → cur_rsi_date (swing-extremum bars, могут
    //     отличаться от ценовых pivot на ±W rsi_tolerance).
    // Повторный click на тот же триггер → сброс (toggle).
    function _clearDivergence() {
      if (!state.divergenceSeries || !state.chart) return;
      state.divergenceSeries.forEach(function(s) {
        try { state.chart.removeSeries(s); } catch (e) {}
      });
      state.divergenceSeries = [];
      var hadSel = state.selectedDivergenceKey != null;
      state.selectedDivergenceKey = null;
      if (hadSel) _applyTriggerFilter();   // была активная → перекрасить маркеры (все белые)
    }

    function _isoToUnix(iso) {
      // "2026-05-14T10:00:00" → unix seconds (UTC, naive treated as UTC)
      if (!iso) return null;
      var s = String(iso).replace('T', ' ');
      // Браузеры по-разному парсят naive ISO — собираем Date вручную.
      var m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
      if (!m) return null;
      return Math.floor(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6]||0)) / 1000);
    }

    function _drawDivergence(trig) {
      if (!state.chart || !state.candleSeries || !trig || !trig.divergence) return;
      var d = trig.divergence;
      var key = trig.bar_date || '';
      // Toggle off if same trigger clicked again
      if (state.selectedDivergenceKey === key) {
        _clearDivergence();
        return;
      }
      _clearDivergence();
      state.selectedDivergenceKey = key;

      // Линии divergence — тонкие белые (LWC lineWidth min=1, имитируем 0.5px
      // через alpha=0.7). Direction не влияет на цвет — однозначно «расхождение
      // цены и RSI», нейтральный белый читается лучше на цветном chart.
      var col = 'rgba(255,255,255,0.85)';

      var prevPriceT = _isoToUnix(d.prev_pivot_date);
      var curPriceT  = _isoToUnix(d.cur_pivot_date);
      // RSI-линия рисуется по viz-точкам (tick-в-tick с ценовым pivot там, где
      // RSI на нём сам образует дивергенцию — 91% случаев; иначе на swing-баре).
      // Fallback на *_rsi_date если backend не прислал viz-поля (старый кэш).
      var prevRsiT   = _isoToUnix(d.prev_rsi_viz_date || d.prev_rsi_date);
      var curRsiT    = _isoToUnix(d.cur_rsi_viz_date  || d.cur_rsi_date);
      var prevRsiV   = (d.prev_rsi_viz != null) ? d.prev_rsi_viz : d.prev_rsi;
      var curRsiV    = (d.cur_rsi_viz  != null) ? d.cur_rsi_viz  : d.cur_rsi;

      // Divergence line on price chart (connects price pivots)
      if (prevPriceT && curPriceT) {
        var priceLine = state.chart.addLineSeries({
          color: col, lineWidth: 1, lineStyle: 0,
          lastValueVisible: false, priceLineVisible: false,
          crosshairMarkerVisible: false,
        });
        var pricePts = [
          { time: prevPriceT, value: d.prev_pivot_price },
          { time: curPriceT,  value: d.cur_pivot_price },
        ].sort(function(a, b) { return a.time - b.time; });
        priceLine.setData(pricePts);
        state.divergenceSeries.push(priceLine);
      }

      // Divergence line on RSI pane (connects RSI swing-extremum bars)
      if (state.rsiSeries && prevRsiT && curRsiT) {
        var rsiLine = state.chart.addLineSeries({
          priceScaleId: 'rsi',
          color: col, lineWidth: 1, lineStyle: 0,
          lastValueVisible: false, priceLineVisible: false,
          crosshairMarkerVisible: false,
          autoscaleInfoProvider: function () { return {priceRange: {minValue: 0, maxValue: 100}}; },
        });
        var rsiPts = [
          { time: prevRsiT, value: prevRsiV },
          { time: curRsiT,  value: curRsiV },
        ].sort(function(a, b) { return a.time - b.time; });
        rsiLine.setData(rsiPts);
        state.divergenceSeries.push(rsiLine);
      }
      // Перекрашиваем маркеры: активная дивергенция (эта) — ярко-белая, остальные бледнее.
      _applyTriggerFilter();
    }

    /** Времена баров текущего ТФ (Set) — кэш инвалидируется сам: ключ = ссылка на
     *  массив свечей, новый payload = новый массив. */
    function _barTimes() {
      var cs = (state.lastData && state.lastData.candles) || [];
      if (state._btSrc !== cs) {
        var s = Object.create(null);
        for (var i = 0; i < cs.length; i++) s[cs[i].time] = 1;
        state._btSrc = cs; state._btSet = s;
      }
      return state._btSet || Object.create(null);
    }

    /** Лежит ли геометрия на шкале текущего ТФ.
     *  Зачем: фигурные сетапы (BEARISH_FLAG / DOUBLE_*) детектятся на 1h и приезжают
     *  маркерами на ЛЮБОЙ ТФ, но их точки заданы часовыми метками. На 15m/5m такие
     *  метки среди баров есть, а на D1 — нет, и LWC роняет line-серию («Value is null»),
     *  обрывая ВЕСЬ рендер: график выходил пустым (инцидент 10.08.2026 — пустая
     *  картинка в TG-разборе). Гейт: не совпало с барами → фигуру не рисуем,
     *  маркер сетапа при этом остаётся. */
    function _ptsOnScale(pts) {
      if (!pts || !pts.length) return false;
      var bt = _barTimes();
      for (var i = 0; i < pts.length; i++) if (!bt[pts[i].time]) return false;
      return true;
    }

    // ── Bearish flag: древко (импульс вниз) + границы канала флага (click-to-inspect) ──
    function _clearBearishFlag() {
      if (!state.bearishFlagSeries || !state.chart) return;
      state.bearishFlagSeries.forEach(function(s) {
        try { state.chart.removeSeries(s); } catch (e) {}
      });
      state.bearishFlagSeries = [];
      state.selectedFlagKey = null;
    }

    /** Геометрия медвежьего флага — ЕДИНЫЙ источник формы для обоих режимов
     *  отрисовки: click-to-inspect (лаборатория) и per-key «показано по умолчанию»
     *  (/setups, 2026-08-02). Возвращает [{pts, color, style, width}]. */
    function _flagLineSpecs(d) {
      var poleCol = 'rgba(224,64,251,0.95)';  // пурпур — древко (импульс вниз)
      var flagCol = 'rgba(224,64,251,0.8)';   // пурпур — границы канала флага
      var pt = _isoToUnix(d.pole_top_date), pb = _isoToUnix(d.pole_bot_date);
      var fs = _isoToUnix(d.flag_start_date), fe = _isoToUnix(d.flag_end_date);
      var out = [];
      // древко: локальный хай → swing-low (сплошная, тонкая)
      if (pt && pb) out.push({pts: [{time: pt, value: d.pole_top},
                                     {time: pb, value: d.pole_bot}],
                              color: poleCol, style: 0, width: 1});
      // канал флага: верх + низ (пунктир) + смыкающие торцы → читается как прямоугольник
      if (fs && fe) {
        [[{time: fs, value: d.flag_top}, {time: fe, value: d.flag_top}],
         [{time: fs, value: d.flag_bot}, {time: fe, value: d.flag_bot}],
         [{time: fs, value: d.flag_bot}, {time: fs, value: d.flag_top}],
         [{time: fe, value: d.flag_bot}, {time: fe, value: d.flag_top}]].forEach(function(p) {
          out.push({pts: p, color: flagCol, style: 2, width: 1});
        });
      }
      return out;
    }

    function _mkFlagSeries(spec) {
      if (!_ptsOnScale(spec.pts)) return null;   // чужой ТФ → не рисуем (см. _ptsOnScale)
      var s = state.chart.addLineSeries({
        color: spec.color, lineWidth: spec.width || 1, lineStyle: spec.style || 0,
        lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false,
      });
      s.setData(spec.pts.slice().sort(function(a, b) { return a.time - b.time; }));
      return s;
    }

    /** Per-key отрисовка флага в модели deSeriesByKey — «показан по умолчанию»
     *  для всех видимых BEARISH_FLAG (как зигзаги DE). Форма — тот же
     *  _flagLineSpecs, что у click-inspect: одна геометрия, два жизненных цикла. */
    function _drawBearishFlagKey(trig) {
      if (!state.chart || !state.candleSeries || !trig || !trig.bearish_flag) return;
      var key = _deKey(trig);
      if (state.deSeriesByKey[key]) return;   // idempotent
      var arr = _flagLineSpecs(trig.bearish_flag).map(_mkFlagSeries).filter(Boolean);
      if (arr.length) state.deSeriesByKey[key] = arr;
    }

    /** Геометрия «скрытого уровня» (лаборатория): импульс + границы зоны паузы,
     *  протянутые вправо до бара возврата — так рисует эту зону наставник владельца.
     *  Возвращает [{pts, color, style, width}] под _mkFlagSeries. */
    function _hlLineSpecs(d) {
      var impCol = 'rgba(38,198,218,0.95)';   // бирюза — импульс, породивший уровень
      var zoneCol = 'rgba(38,198,218,0.85)';  // бирюза — границы зоны (сам «скрытый уровень»)
      var is = _isoToUnix(d.imp_start_date), ie = _isoToUnix(d.imp_end_date);
      var zs = _isoToUnix(d.zone_start_date), ze = _isoToUnix(d.zone_end_date);
      var en = _isoToUnix(d.entry_date);
      var out = [];
      // импульс: от открытия первой свечи до закрытия последней (сплошная, тонкая)
      if (is && ie) out.push({pts: [{time: is, value: d.imp_start_price},
                                     {time: ie, value: d.imp_end_price}],
                              color: impCol, style: 0, width: 1});
      // САМА ПАУЗА (1-3 свечи, из которых и построен уровень) — жирная рамка со всех
      // четырёх сторон: при длинном ожидании возврата линии тянутся через пол-экрана,
      // и без выделенной коробки не видно, где, собственно, зона (репорт владельца 19.08).
      if (zs && ze) {
        [[{time: zs, value: d.zone_top}, {time: ze, value: d.zone_top}],
         [{time: zs, value: d.zone_bot}, {time: ze, value: d.zone_bot}],
         [{time: zs, value: d.zone_bot}, {time: zs, value: d.zone_top}],
         [{time: ze, value: d.zone_bot}, {time: ze, value: d.zone_top}]].forEach(function(pp) {
          out.push({pts: pp, color: zoneCol, style: 0, width: 2});
        });
      }
      // «хвост» уровня — от конца паузы вправо до бара возврата, пунктиром и тоньше:
      // это ожидание цены, а не сама зона
      if (ze && en && en > ze) {
        [[{time: ze, value: d.zone_top}, {time: en, value: d.zone_top}],
         [{time: ze, value: d.zone_bot}, {time: en, value: d.zone_bot}]].forEach(function(pp) {
          out.push({pts: pp, color: zoneCol, style: 2, width: 1});
        });
      }
      return out;
    }

    function _drawHiddenLevelKey(trig) {
      if (!state.chart || !state.candleSeries || !trig || !trig.hidden_level) return;
      var key = _deKey(trig);
      if (state.deSeriesByKey[key]) return;   // idempotent
      var arr = _hlLineSpecs(trig.hidden_level).map(_mkFlagSeries).filter(Boolean);
      if (arr.length) state.deSeriesByKey[key] = arr;
    }

    // ── Double bottom/top: зигзаг движения (P→1-я верш→шея→2-я верш) + линия шеи ──
    // Per-figure модель: зигзаги показаны ПО УМОЛЧАНИЮ для всех видимых фигур
    // (state.deSeriesByKey). Клик по маркеру скрывает/показывает конкретную
    // (state.deHiddenKeys). Управляется _syncDoubleExtremeZigzags из _applyTriggerFilter.

    // DE-семейство: prod DOUBLE_EXTREME_MTF (амбер) + 3 admin-версии DE_BEARISH_FLAG /
    // DE_DOUBLE_BOTTOM / DE_DOUBLE_TOP (свои цвета из t.color). Каждый — своя серия
    // зигзага (ключ тип+бар), чтобы можно было редактировать/скрывать независимо.
    function _isDeType(t) {
      return t && (t.trigger === 'DOUBLE_EXTREME_MTF'
                   // ML-фигурные на /setups (2026-08-02): та же геометрия
                   // «две вершины + шея» → тот же зигзаг, координаты из trig_json.
                   || t.trigger === 'DOUBLE_TOP' || t.trigger === 'DOUBLE_BOTTOM'
                   || (typeof t.trigger === 'string' && t.trigger.indexOf('DE_') === 0));
    }
    // BEARISH_FLAG — другая геометрия (древко + прямоугольник флага), своя рисовалка.
    function _isFlagType(t) { return t && t.trigger === 'BEARISH_FLAG' && t.bearish_flag; }
    // HIDDEN_LEVEL — зона паузы внутри импульса (лаборатория): своя геометрия.
    function _isHlType(t) { return t && t.trigger === 'HIDDEN_LEVEL' && t.hidden_level; }
    function _deKey(t) { return (t.trigger || '') + '|' + (t.bar_date || ''); }
    function _dimHex(hex) {   // hex → rgba с alpha 0.4 (dim скрытого кликом маркера)
      if (typeof hex !== 'string' || hex[0] !== '#' || hex.length < 7) return 'rgba(255,255,255,0.4)';
      return 'rgba(' + parseInt(hex.slice(1, 3), 16) + ',' + parseInt(hex.slice(3, 5), 16)
             + ',' + parseInt(hex.slice(5, 7), 16) + ',0.4)';
    }

    function _clearAllDoubleExtreme() {
      Object.keys(state.deSeriesByKey || {}).forEach(function(k) {
        (state.deSeriesByKey[k] || []).forEach(function(s) {
          try { state.chart.removeSeries(s); } catch (e) {}
        });
      });
      state.deSeriesByKey = {};
    }

    function _clearDoubleExtremeKey(key) {
      (state.deSeriesByKey[key] || []).forEach(function(s) {
        try { state.chart.removeSeries(s); } catch (e) {}
      });
      delete state.deSeriesByKey[key];
    }

    function _drawDoubleExtremeKey(trig) {
      if (!state.chart || !state.candleSeries || !trig || !trig.double_extreme) return;
      var key = _deKey(trig);                 // тип+бар → своя серия у каждой версии
      if (state.deSeriesByKey[key]) return;   // уже нарисована — idempotent
      // Цвет зигзага = цвет триггера (prod-DE амбер, версии — свои); 1px.
      var zzHex = trig.color || '#ffc107';
      var d = trig.double_extreme;
      var pT = _isoToUnix(d.prev_pivot_date), cT = _isoToUnix(d.cur_pivot_date),
          nT = _isoToUnix(d.neckline_date), priorT = _isoToUnix(d.prior_move_date);
      // Зигзаг движения: P (старт) → 1-я вершина → шея → 2-я вершина
      var zz = [];
      if (priorT && d.prior_move_price != null) zz.push({time: priorT, value: d.prior_move_price});
      if (pT) zz.push({time: pT, value: d.prev_pivot_price});
      if (nT) zz.push({time: nT, value: d.neckline_price});
      if (cT) zz.push({time: cT, value: d.cur_pivot_price});
      var seen = {}, pts = [];
      zz.sort(function(a, b) { return a.time - b.time; }).forEach(function(p) {
        if (p.time != null && !seen[p.time]) { seen[p.time] = 1; pts.push(p); }
      });
      // Тот же гейт, что у флага: часовая фигура на D1-шкале уронила бы весь рендер.
      if (!_ptsOnScale(pts)) return;
      var arr = [];
      if (pts.length >= 2) {
        var zline = state.chart.addLineSeries({color: zzHex, lineWidth: 1, lineStyle: 0,
          lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false});
        zline.setData(pts);
        arr.push(zline);
      }
      // Линия шеи — горизонтальный пунктир от 1-й до 2-й вершины (референс-уровень)
      if (pT && cT && d.neckline_price != null) {
        var nline = state.chart.addLineSeries({color: zzHex, lineWidth: 1,
          lineStyle: 2, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false});
        nline.setData([{time: pT, value: d.neckline_price}, {time: cT, value: d.neckline_price}]
                        .sort(function(a, b) { return a.time - b.time; }));
        arr.push(nline);
      }
      // Уровни сделки (DOUBLE_BOTTOM, вход по пробою шеи): стоп (красный) + measured-move
      // цель = взвод трейла (зелёный). Короткие горизонтальные сегменты вправо от входа.
      if (d.entry_time && (d.sl_price != null || d.tp_price != null)) {
        var cs = (state.lastData && state.lastData.candles) || [], tEnd = d.entry_time;
        for (var ci = 0; ci < cs.length; ci++) {
          if (cs[ci].time >= d.entry_time) { tEnd = cs[Math.min(ci + 40, cs.length - 1)].time; break; }
        }
        if (tEnd > d.entry_time) {
          if (d.sl_price != null) {
            var slL = state.chart.addLineSeries({color: 'rgba(247,80,73,0.95)', lineWidth: 2,
              lineStyle: 0, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false});
            slL.setData([{time: d.entry_time, value: d.sl_price}, {time: tEnd, value: d.sl_price}]);
            arr.push(slL);
          }
          if (d.tp_price != null) {
            var tpL = state.chart.addLineSeries({color: 'rgba(63,184,79,0.95)', lineWidth: 2,
              lineStyle: 0, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false});
            tpL.setData([{time: d.entry_time, value: d.tp_price}, {time: tEnd, value: d.tp_price}]);
            arr.push(tpL);
          }
        }
      }
      if (arr.length) state.deSeriesByKey[key] = arr;
    }

    // Синхронизация: рисуем зигзаги всех видимых DE-фигур (кроме скрытых кликом),
    // убираем зигзаги фигур, чьи маркеры скрыты (фильтр типа / solo / смена тикера).
    function _syncDoubleExtremeZigzags() {
      if (!state.chart || !state.lastData) return;
      var trigs = (state.lastData.triggers || []).filter(function(t) {
        return ((_isDeType(t) && t.double_extreme) || _isFlagType(t) || _isHlType(t)) && _isTriggerVisible(t);
      });
      var visibleKeys = {};
      trigs.forEach(function(t) { visibleKeys[_deKey(t)] = 1; });
      Object.keys(state.deSeriesByKey).forEach(function(k) {
        if (!visibleKeys[k]) _clearDoubleExtremeKey(k);   // маркер скрыт → убрать зигзаг
      });
      trigs.forEach(function(t) {
        var key = _deKey(t);
        if (state.deHiddenKeys[key]) _clearDoubleExtremeKey(key);   // скрыта кликом
        else if (_isFlagType(t)) _drawBearishFlagKey(t);            // древко + флаг
        else if (_isHlType(t)) _drawHiddenLevelKey(t);              // импульс + зона паузы
        else _drawDoubleExtremeKey(t);                              // зигзаг + шея
      });
    }

    // Toggle по клику на маркер: скрыть/показать зигзаг конкретной фигуры.
    // Ключ = тип+бар (_deKey) — чтобы prod-DE и 3 версии тоглились независимо.
    function _toggleDoubleExtreme(trig) {
      var key = _deKey(trig);
      if (state.deHiddenKeys[key]) delete state.deHiddenKeys[key];
      else state.deHiddenKeys[key] = 1;
      _applyTriggerFilter();   // ре-синк зигзагов + перекраска маркера (dim/bright)
    }

    // ── S/R text labels (HTML overlay, не на ценовой шкале) ──
    // LWC v4 рендерит title price-line'а только как часть axisLabel-плашки.
    // Чтобы вернуть метки S1/R1/S2 без шильдов на ценовой оси — рисуем их
    // HTML-оверлеем поверх chart-контейнера: позиция Y берётся из
    // priceToCoordinate(), позиция X — фиксированный отступ от правого края
    // (~10px). При зуме/скролле обновляем через subscribeVisibleLogicalRangeChange.
    // Расшифровка source MTF-уровня → русский текст для title-тултипа.
    // source: '+'-склейка источников (PDH/PDL/PWH/PWL/swing/density/Nd_high/Nw_low/
    // wclose_N). touches — число касаний.
    function _mtfPlural(n) {
      n = Math.abs(n) % 100; var n1 = n % 10;
      if (n > 10 && n < 20) return 'касаний';
      if (n1 > 1 && n1 < 5) return 'касания';
      if (n1 === 1) return 'касание';
      return 'касаний';
    }
    // «с 12 авг» — когда уровень сформировался. Год добавляем ТОЛЬКО если он не
    // текущий: на 1h/15m/5m в окне графика всегда один год и год был бы шумом, а на
    // D1/4h видно 2-3 года (700/600 баров), там без него «12 сен» неоднозначно.
    function _levelSince(iso) {
      if (!iso) return '';
      var d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      var M = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];
      var s = 'с ' + d.getDate() + ' ' + M[d.getMonth()];
      var now = new Date();
      if (d.getFullYear() !== now.getFullYear()) s += " '" + String(d.getFullYear()).slice(-2);
      return s;
    }
    function _mtfSourceTooltip(source, touches) {
      function tok(t) {
        if (t === 'PDH') return 'Максимум пред. дня';
        if (t === 'PDL') return 'Минимум пред. дня';
        if (t === 'PWH') return 'Максимум пред. недели';
        if (t === 'PWL') return 'Минимум пред. недели';
        if (t === 'swing') return 'Свинг-экстремум';
        if (t === 'density') return 'Зона плотности (консолидация)';
        var m;
        if ((m = t.match(/^(\d+)d_high$/))) return 'Максимум за ' + m[1] + ' дн.';
        if ((m = t.match(/^(\d+)d_low$/)))  return 'Минимум за ' + m[1] + ' дн.';
        if ((m = t.match(/^(\d+)w_high$/))) return 'Максимум за ' + m[1] + ' нед.';
        if ((m = t.match(/^(\d+)w_low$/)))  return 'Минимум за ' + m[1] + ' нед.';
        if ((m = t.match(/^wclose_(\d+)$/))) return 'Закрытие недели ' + m[1] + ' назад';
        return t;
      }
      var txt = String(source || '').split('+').map(tok).join(' + ');
      if (touches) txt += ' · ' + touches + ' ' + _mtfPlural(touches);
      return txt;
    }
    // Короткий код уровня (≤5 знаков) для подписи на графике; полное описание — в тултипе.
    // Для комбо-источника берём самый специфичный токен (опорные точки > swing > density).
    function _shortLevelCode(source) {
      var s = String(source || ''), m;
      if (/PDH/.test(s)) return 'PDH';
      if (/PDL/.test(s)) return 'PDL';
      if (/PWH/.test(s)) return 'PWH';
      if (/PWL/.test(s)) return 'PWL';
      if ((m = s.match(/(\d+)d_high/))) return m[1] + 'H';
      if ((m = s.match(/(\d+)d_low/)))  return m[1] + 'L';
      if ((m = s.match(/(\d+)w_high/))) return m[1] + 'wH';
      if ((m = s.match(/(\d+)w_low/)))  return m[1] + 'wL';
      if (/wclose/.test(s)) return 'wc';
      if (/swing_w1/.test(s)) return 'swW';
      if (/swing/.test(s)) return 'sw';
      if (/density/.test(s)) return 'dn';
      return s.slice(0, 5);
    }
    // Приоритет кода (выбор главного при слиянии конфлюэнса): меньше = важнее.
    function _codeRank(code) {
      if (/^(PDH|PDL|PWH|PWL)$/.test(code)) return 1;
      if (/^\d+w?[HL]$/.test(code)) return 2;          // 20H/20L/7wH/7wL
      if (/^[RS]\d/.test(code)) return 3;              // S/R пивоты (R1,S2…)
      if (code === 'wc' || code === 'swW') return 4;
      if (code === 'sw') return 5;
      return 6;                                         // dn и прочее
    }

    function _renderSRLabels() {
      if (!chartEl) return;
      // Удаляем старый overlay и (если есть) выходим если SR-слой выключен
      var old = chartEl.querySelector('.sr-labels-overlay');
      if (old) old.remove();
      if (!(state.layers.sr || state.layers.mtf) || !state.lastData || !state.lastData.levels
          || !state.candleSeries) return;

      // Сделаем контейнер позиционированным, если ещё не
      if (getComputedStyle(chartEl).position === 'static') {
        chartEl.style.position = 'relative';
      }
      var overlay = document.createElement('div');
      overlay.className = 'sr-labels-overlay';
      overlay.style.cssText = 'position:absolute;inset:0;pointer-events:none;'
        + 'overflow:hidden;z-index:5';
      chartEl.appendChild(overlay);

      // Мгновенный (без нативной задержки) тултип уровня — отдельный div слева от подписи
      // (подписи у правого края). state._levelTip persist между рендерами.
      var fpx = function (p) { var a = Math.abs(p); return a >= 100 ? p.toFixed(1) : a >= 1 ? p.toFixed(2) : a >= 0.01 ? p.toFixed(4) : p.toFixed(6); };
      function _showLevelTip(html, anchorEl) {
        var t = state._levelTip;
        if (!t || !t.isConnected) {
          t = document.createElement('div');
          t.style.cssText = 'position:absolute;z-index:60;pointer-events:none;display:none;'
            + 'background:rgba(20,32,45,0.97);border:1px solid #2a3a4d;border-radius:6px;'
            + 'padding:6px 9px;font-size:11px;line-height:1.45;color:#e6edf2;max-width:240px;'
            + 'box-shadow:0 6px 20px rgba(0,0,0,0.5);font-family:"JetBrains Mono",monospace';
          chartEl.appendChild(t); state._levelTip = t;
        }
        t.innerHTML = html; t.style.display = 'block';
        var ar = anchorEl.getBoundingClientRect(), cr = chartEl.getBoundingClientRect();
        var left = (ar.left - cr.left) - (t.offsetWidth || 200) - 8; if (left < 2) left = 2;
        var top = (ar.top - cr.top) - 2;
        var maxTop = (chartEl.clientHeight || 600) - (t.offsetHeight || 40) - 2;
        if (top > maxTop) top = Math.max(2, maxTop);
        if (top < 2) top = 2;
        t.style.left = left + 'px'; t.style.top = top + 'px';
      }
      function _hideLevelTip() { if (state._levelTip) state._levelTip.style.display = 'none'; }

      function update() {
        if (!overlay.isConnected || !state.candleSeries) return;
        // lastData обнуляется на время перезагрузки графика (например, при
        // переключении на «Всю историю»), а подписка LWC на изменение диапазона
        // продолжает звать update → падало на state.lastData.levels (найдено 25.08).
        if (!state.lastData || !state.lastData.levels) { overlay.innerHTML = ''; return; }
        overlay.innerHTML = '';
        _hideLevelTip();
        var psWidth = 56;
        try { psWidth = state.chart.priceScale('right').width() || 56; } catch(e) {}
        // Свечная панель по вертикали — из scaleMargins ('right'). Подписи рисуем ТОЛЬКО для
        // уровней, чья y попадает в неё (= реальный видимый диапазон ОСИ, а не свечей). Иначе:
        // нижние за кадром (у LEAS десятки уровней ниже оси) экстраполируются под панель и
        // валятся кашей; верхние (выше свечей, но в оси — над SL) раньше теряли подписи.
        var H = 14;
        var paneTop = 0, paneBot = (chartEl.clientHeight || 600);
        try {
          var sm = state.chart.priceScale('right').options().scaleMargins;
          var Hp = chartEl.clientHeight || 600;
          if (sm) { paneTop = (sm.top || 0) * Hp; paneBot = (1 - (sm.bottom || 0)) * Hp; }
        } catch(e) {}
        function inPane(y) { return y >= paneTop - H && y <= paneBot + H; }
        // 1) Собрать подписи (MTF + S/R) В ВИДИМОМ ОКНЕ. MTF первыми → база при слиянии.
        //    code = короткий код (≤5 знаков); full = строки полного описания для тултипа.
        var items = [];
        if (state.layers.mtf) (state.lastData.levels.mtf || []).forEach(function(lv) {
          var y = null; try { y = state.candleSeries.priceToCoordinate(lv.price); } catch(e) { return; }
          if (y === null || y === undefined || !inPane(y)) return;
          var isHi = state.highlightLevel != null
            && Math.abs(lv.price - state.highlightLevel) <= Math.max(1e-6, Math.abs(state.highlightLevel) * 0.0015);
          items.push({ price: lv.price, y: y, kind: 'mtf', hi: isHi, touches: lv.touches || 0,
            code: _shortLevelCode(lv.source),
            full: [_mtfSourceTooltip(lv.source, lv.touches), _levelSince(lv.start_date)].filter(Boolean),
            color: isHi ? '#ffd24a' : '#d4a016' });
        });
        if (state.layers.sr) (state.lastData.levels.sr || []).forEach(function(lv) {
          if ((lv.touches || 0) <= 2) return;   // слабые пивоты (≤2) — шум, не показываем (как и линии)
          var y = null; try { y = state.candleSeries.priceToCoordinate(lv.price); } catch(e) { return; }
          if (y === null || y === undefined || !inPane(y)) return;
          var isS = lv.type === 'S';
          var desc = (isS ? 'Поддержка ' : 'Сопротивление ') + lv.label
            + (lv.touches ? ' · ' + lv.touches + ' ' + _mtfPlural(lv.touches) : '');
          items.push({ price: lv.price, y: y, kind: 'sr', hi: false, touches: lv.touches || 0,
            code: lv.label, full: [desc, _levelSince(lv.start_date)].filter(Boolean), color: isS ? '#5cb86a' : '#d06060' });
        });
        if (!items.length) return;
        // 2) Слияние совпадающих по цене (конфлюэнс) → код важнейшего + «+»; полное — в тултипе.
        items.sort(function(a, b) { return a.price - b.price; });
        var merged = [];
        items.forEach(function(it) {
          var last = merged.length ? merged[merged.length - 1] : null;
          var mtol = Math.max(0.01, Math.abs(it.price) * 0.0005);   // «та же цена» (~0.05%)
          if (last && Math.abs(it.price - last.price) <= mtol) {
            if (_codeRank(it.code) < _codeRank(last.code)) last.code = it.code;
            last.conf = true;
            last.full = last.full.concat(it.full);
            last.hi = last.hi || it.hi;
            last.touches = Math.max(last.touches, it.touches);
            if (it.hi) last.color = '#ffd24a';
            else if (it.kind === 'mtf' && last.kind !== 'mtf') { last.color = '#d4a016'; last.kind = 'mtf'; }
          } else {
            merged.push({ price: it.price, y: it.y, kind: it.kind, hi: it.hi, touches: it.touches,
              code: it.code, full: it.full.slice(), color: it.color, conf: false });
          }
        });
        // 3) Анти-overlap по y: проход вниз (раздвигаем наезжающие), затем вверх (кламп по низу панели).
        merged.sort(function(a, b) { return a.y - b.y; });
        for (var i = 1; i < merged.length; i++) {
          if (merged[i].y < merged[i-1].y + H) merged[i].y = merged[i-1].y + H;
        }
        for (var j = merged.length - 1; j >= 1; j--) {
          if (merged[j].y > paneBot - H) merged[j].y = paneBot - H;
          if (merged[j-1].y > merged[j].y - H) merged[j-1].y = merged[j].y - H;
        }
        // 4) Рендер: КОРОТКИЙ код (≤5 знаков, конфлюэнс → «+») + мгновенный тултип (mouseenter)
        //    с полным описанием. Подсвеченный уровень сетапа — ярче+жирнее+рамка.
        merged.forEach(function(it) {
          var lbl = document.createElement('div');
          lbl.style.cssText = 'position:absolute;right:' + (psWidth + 4)
            + 'px;top:' + (Math.round(it.y) - 7)
            + 'px;font-size:10px;font-weight:' + (it.hi ? '700' : '600') + ';letter-spacing:.04em;'
            + 'color:' + it.color + ';'
            + 'background:' + (it.hi ? 'rgba(212,160,22,0.22)' : 'rgba(12,17,22,0.55)') + ';'
            + 'padding:0 4px;border-radius:2px;' + (it.hi ? 'box-shadow:0 0 0 1px rgba(255,210,74,0.6);' : '')
            + 'line-height:13px;white-space:nowrap;pointer-events:auto;cursor:help;'
            + 'font-family:"JetBrains Mono",monospace';
          lbl.textContent = it.code + (it.conf ? '+' : '');
          var tip = '<b>' + fpx(it.price) + '</b><br>' + it.full.join('<br>');
          lbl.addEventListener('mouseenter', function() { _showLevelTip(tip, lbl); });
          lbl.addEventListener('mouseleave', _hideLevelTip);
          overlay.appendChild(lbl);
        });
      }
      state._srLabelsUpdate = update;   // для setHighlightLevel + live-update — пересобрать подписи без reload
      update();
      // Авто-fit оси по Y происходит АСИНХРОННО после setData (а также при спайке / live-
      // обновлении цены) и НЕ меняет x-диапазон → subscribeVisibleLogicalRangeChange НЕ
      // срабатывает, и подписи остаются на старых y (видно на CHGZ: спайк расширил ось,
      // линии уехали от подписей). Перевызываем update() на следующих кадрах — пересчёт y
      // после осадки fit (rAF) и после прихода первого live-price (setTimeout).
      try { requestAnimationFrame(update); } catch(e) {}
      setTimeout(update, 200);
      setTimeout(update, 700);
      // Смена x-окна (зум/скролл) — пересчёт y. (Auto-fit y без смены x ловим выше + в live-update.)
      try { state.chart.timeScale().subscribeVisibleLogicalRangeChange(update); } catch(e) {}
    }

    // ── Marker hover tooltip (desktop): multi-line ──
    function setupMarkerTooltip() {
      if (!tooltipEl || !state.chart) return;
      state.chart.subscribeCrosshairMove(function(param) {
        if (!param.time || !param.point) { tooltipEl.style.display = 'none'; return; }
        // Tolerance ±1 бар (как в subscribeClick). VWAP_REV-маркеры несут M5-время
        // (напр. 13:25), не совпадающее с временем 15m/1h-свечи под курсором —
        // exact-match никогда не срабатывал → tooltip не показывался. Visible-only:
        // не показываем tooltip для скрытых DIVERGENCE / отфильтрованных типов.
        var ttol = state.currentTf === '4h' ? 14400
                 : state.currentTf === '1h' ? 3600
                 : state.currentTf === '15m' ? 900
                 : state.currentTf === '5m' ? 300 : 86400;
        var hits = state.triggersData.filter(function(t) {
          if (!_isTriggerVisible(t)) return false;
          if (Math.abs(t.time - param.time) <= ttol) return true;
          // solo: стрелка входа стоит на баре входа, не сигнала — отвечаем и там
          return !!(state.soloKey && t._soloEntryTime != null
                    && Math.abs(t._soloEntryTime - param.time) <= ttol);
        });
        (state.exitMarkers || []).forEach(function(m) {
          if (_isTriggerVisible(m) && (Math.abs(m.time - param.time) <= ttol
              || (m._drawnTime != null && m._drawnTime === param.time))) hits.push(m);
        });
        // Отсечка — свойство бумаги, не сетапа: показываем независимо от фильтров.
        (state.dividendMarks || []).forEach(function(d) {
          if (Math.abs(d.time - param.time) <= ttol) hits.push({_div: true, d: d});
        });
        if (hits.length === 0) { tooltipEl.style.display = 'none'; return; }
        var lines = hits.map(function(t) {
          if (t._div) {
            // yield_at_ex хранится ДОЛЕЙ (0.1357 = 13.57%) — приводим к процентам.
            var dd = t.d, det = [];
            if (dd.amount != null) {
              var a = Number(dd.amount);
              det.push((Math.abs(a - Math.round(a)) < 1e-9 ? String(Math.round(a)) : a.toFixed(2)) + ' \u20bd');
            }
            if (dd.yield_pct != null) det.push((dd.yield_pct * 100).toFixed(1) + '%');
            det.push(dd.record_date);
            return '<span style="color:#d4a016"><b>Внимание! Завтра дивидендная отсечка</b></span>'
                 + '<br><span style="color:#8a939e;font-size:11px">' + _esc(det.join(' \u00b7 ')) + '</span>';
          }
          // Q2: маркер выхода — показываем его tip («Выход: +0.56R (трейл) @…»).
          if (t.exit) {
            return '<span style="color:' + t.color + '"><b>' + _esc(t.tip || 'Выход') + '</b></span>';
          }
          // tip_title — точный вид дивергенции (Бычья / Скрытая медвежья…); иначе общий.
          var name = t.tip_title || _TRIGGER_RU[t.trigger] || t.trigger;
          // «★ N» (счётчик вариантов стратегии) убран — выглядел как рейтинг (2026-06-14).
          // Человекопонятный tip (вариант B); fallback на технический detail
          // для legacy-записей без tip-поля.
          var sub = t.tip || (t.detail && t.detail !== t.trigger ? t.detail : '');
          var subHtml = sub
            ? '<br><span style="color:#8a939e;font-size:11px;white-space:pre-line">' + _esc(sub) + '</span>'
            : '';
          // /ml-setups: 3-я строка «Вход <цена>» (t._entryTip). /setups не задаёт → нет.
          var entryHtml = t._entryTip
            ? '<br><span style="color:#8a939e;font-size:11px">' + _esc(t._entryTip) + '</span>'
            : '';
          return '<span style="color:' + t.color + '"><b>' + _esc(name) + '</b></span>' + subHtml + entryHtml;
        });
        tooltipEl.innerHTML = lines.join('<br>');
        tooltipEl.style.display = 'block';
        // Reading offsetHeight forces sync layout — даёт актуальные размеры
        // после смены innerHTML и display:block.
        var ttH = tooltipEl.offsetHeight || 35;
        var ttW = tooltipEl.offsetWidth  || 0;
        // Зажимаем left ≥ 8: при широком tooltip (clientWidth − ttW − 10) может
        // уйти в минус → левый край вылазил за границу графика (обрезался).
        var x = Math.max(8, Math.min(param.point.x + 16, chartEl.clientWidth - ttW - 10));
        // Bottom tooltip's edge должен быть на 8px выше курсора, чтобы курсор
        // не наезжал на текст (раньше высокие tooltip'ы накрывали курсор).
        var y = Math.max(param.point.y - ttH - 8, 5);
        tooltipEl.style.left = x + 'px';
        tooltipEl.style.top = y + 'px';
      });
    }

    // ── Live Price ──
    function startLivePrice(ticker) {
      if (state.liveInterval) clearInterval(state.liveInterval);
      state.livePriceLine = null;
      // Исторический срез — текущую цену не дорисовываем (линия на последнем баре
      // окна, оборвавшегося в прошлом, была бы враньём). Судим по окну, а не по
      // самому факту around: solo-просмотр свежего сетапа (журнал sim-трейда,
      // /setups) грузится с around, но окно доходит до «сейчас» — там живая цена
      // нужна. Флаг считает бэкенд (meta.window_is_current); старый payload без
      // поля → падаем на прежнее поведение «around ⇒ история».
      var _cur = (state.lastData && state.lastData.meta)
                 ? state.lastData.meta.window_is_current : undefined;
      if (state.backtestMode && _cur !== true) return;

      function update() {
        // ISS не отдаёт candles для interval=15 и interval=5, но backend
        // достраивает synthetic forming-candle из последнего бара БД + price.
        // Для D1 fresh_candles не нужны — только spot price.
        var tfParam = (state.currentTf === '4h'
                    || state.currentTf === '1h'
                    || state.currentTf === '15m'
                    || state.currentTf === '5m')
                      ? state.currentTf : 'D1';
        fetch('/api/ta/live-price/' + ticker + '?tf=' + tfParam
              + (state.liveRealtime ? '&realtime=1' : ''))
          .then(function(r) { return r.json(); })
          .then(function(d) {
            if (!d.price || !state.candleSeries) return;
            if (state.livePriceLine) {
              try { state.candleSeries.removePriceLine(state.livePriceLine); } catch(e) {}
            }
            // Сплошная белая линия (полупрозрачную rgba линию LWC v4.2 в Safari
            // рендерит sub-pixel и теряет визуально). Плашка на оси
            // полупрозрачная — не загораживает ценовые подписи ниже/выше.
            state.livePriceLine = state.candleSeries.createPriceLine({
              price: d.price, color: '#ffffff', lineWidth: 1, lineStyle: 0,
              title: '', axisLabelVisible: true,
              axisLabelColor: 'rgba(255,255,255,0.4)', axisLabelTextColor: '#0c1116',
            });
            // fresh_candles подмешиваем для всех intraday TF (1h/15m/5m) —
            // backend кладёт туда synthetic forming-candle. Для D1 пропускаем.
            //
            // Используем series.update() (incremental, single-bar) вместо
            // setData(rebuilt) (full replace). Причина: setData триггерит
            // full re-render всех серий на shared price scale → во время
            // transient'а divergence-линия может временно отрисоваться в
            // wrong Y position (выше chart'а) пока priceScale auto-fit
            // стабилизируется. update() — incremental APPEND/REPLACE по
            // time, не вызывает rescale.
            if (d.fresh_candles && d.fresh_candles.length > 0
                && state.currentTf !== 'D1' && state.lastData) {
              // Sorted чтобы update() шёл в порядке возрастания времени —
              // LWC требует append'ить только бары с time >= last.
              var freshSorted = d.fresh_candles.slice().sort(
                function(a, b) { return a.time - b.time; });
              freshSorted.forEach(function(fc) {
                var isUp = fc.close >= fc.open;
                var h = fc.high, l = fc.low;
                var minSpread = fc.close * 0.003;
                if ((h - l) < minSpread && fc.close > 0) {
                  var mid = (h + l) / 2 || fc.close;
                  h = mid + minSpread / 2; l = mid - minSpread / 2;
                }
                try {
                  state.candleSeries.update({
                    time: fc.time, open: fc.open, high: h, low: l, close: fc.close,
                    color: isUp ? '#3fb84f' : '#f75049',
                    borderColor: isUp ? '#3fb84f' : '#f75049',
                    wickColor:   isUp ? '#3fb84f' : '#f75049',
                  });
                  if (state.volumeSeries && fc.volume > 0) {
                    var vColor = isUp
                      ? 'rgba(63,184,79,0.5)' : 'rgba(247,80,73,0.5)';
                    state.volumeSeries.update({
                      time: fc.time, value: fc.volume, color: vColor,
                    });
                  }
                } catch (e) {
                  // Fallback на случай если update() rejected (time < last).
                  // Тихо игнорируем — на след. polling cycle перепробуем.
                }
              });
              // Live-обновление свечи могло сдвинуть авто-fit оси по Y → пересчитать
              // позиции подписей уровней (иначе они отстают от линий, как на CHGZ-спайке).
              if (state._srLabelsUpdate) { try { state._srLabelsUpdate(); } catch(e) {} }
            }
          }).catch(function() {});
      }
      update();
      // Динамический интервал refresh — чем короче TF, тем чаще обновляем
      // forming-свечу. 5M: 15с (1/20 длины бара), 15M: 30с, остальные: 60с.
      var intervalMs = state.currentTf === '5m' ? 15000
                     : state.currentTf === '15m' ? 30000 : 60000;
      state.liveInterval = setInterval(update, intervalMs);

      // Возврат на вкладку — обновляем немедленно, не дожидаясь тика.
      // Браузер тормозит (а после нескольких минут и вовсе замораживает)
      // таймеры фоновых вкладок, поэтому вернувшийся пользователь видит
      // график таким, каким тот застыл. На часовом ТФ это особенно заметно:
      // бар текущего часа в БД отсутствует (при загрузке свечей форминг
      // отбрасывается), он существует ТОЛЬКО как эта дорисовка — застывший
      // таймер означает «последней свечи просто нет».
      // Тот же приём уже применён к опросу авторизации (tg-login).
      if (state.liveVisHandler) {
        document.removeEventListener('visibilitychange', state.liveVisHandler);
      }
      state.liveVisHandler = function () {
        if (!document.hidden) update();
      };
      document.addEventListener('visibilitychange', state.liveVisHandler);
    }

    // ── Render одного trigger-блока (desktop trig-side + mobile bottom sheet) ──
    // Один вариант, формат как на /setups и в TG-рассылке:
    //   [Название]  (цветной бейдж)              [время]
    //   ШОРТ · вход 2801.00
    //   Потенциал: 10.93R · риск ограничен 1R
    //   Серия из 20 сетапов: вероятность ~85%
    function _renderTriggerBlock(tr) {
      // Дата + время (окно карточки — неделя, сетапы за разные дни).
      var _d = _fmtRuDate(tr.bar_date), _t = _fmtBarTime(tr.bar_date);
      var time = _d ? (_d + ', ' + _t) : _t;
      // Заголовок — точное имя вида (tip_title, напр. «Двойное дно») либо
      // русское имя типа из _TRIGGER_RU.
      var titleText = tr.tip_title || _TRIGGER_RU[tr.trigger] || tr.trigger;
      var bg = tr.color || '#d4a016';
      var fg = _textOn(bg);

      // Направление стратегии (final_direction может ≠ direction триггера —
      // инвертированные ячейки каталога).
      var fd = tr.final_direction || tr.direction;
      var dir = fd === 'LONG' ? 'ЛОНГ' : 'ШОРТ';
      var dirColor = fd === 'LONG' ? '#3fb84f' : '#f75049';

      // Вход — entry_price, иначе уровень.
      var entry = (tr.entry_price != null) ? tr.entry_price
                : (tr.level_price != null ? tr.level_price : null);
      var entryStr = (entry != null) ? ' · вход ' + _fmtPrice(entry) : '';

      var dirLine = '<div class="trig-setup-dir">'
        + '<span style="color:' + dirColor + ';font-weight:700">' + dir + '</span>'
        + '<span class="trig-setup-entry">' + entryStr + '</span>'
        + '</div>';

      // Потенциал / серия — SSOT setup_stats (как /setups + TG). Нет статы → опускаем.
      var s = tr.series;
      var statLines = '';
      if (s && s.potential != null) {
        statLines += '<div class="trig-setup-potential">Потенциал: <b>'
          + s.potential.toFixed(2) + 'R</b> · риск ограничен 1R</div>';
        if (s.prob20 != null) {
          var sn = (state.lastData && state.lastData.series_n) || 20;
          statLines += '<div class="trig-setup-series">Серия из ' + sn
            + ' сетапов: вероятность ~' + s.prob20 + '%</div>';
        }
      }

      return '<div class="trig-block">'
        + '<div class="trig-block-time">'
          + '<span class="trig-name-badge" style="background:' + bg + ';color:' + fg + '">'
            + _esc(titleText) + '</span>'
          + '<span class="trig-time">' + time + '</span>'
        + '</div>'
        + dirLine
        + statLines
        + '</div>';
    }

    function _renderTriggerList(triggers, opts) {
      opts = opts || {};
      var emptyMsg = opts.emptyMsg || 'За неделю сетапов нет';
      if (!triggers || triggers.length === 0) {
        return '<div class="trig-empty">' + emptyMsg + '</div>';
      }
      var arr = triggers.slice();
      arr.sort(function(a, b) { return b.time - a.time; });
      return '<div class="trig-list">' + arr.map(_renderTriggerBlock).join('') + '</div>';
    }

    // ── Trig-side panel (desktop, справа от графика) ──
    // Режим рынка теперь живёт в шапке графика; здесь только триггеры за день.
    function buildTrigSideHtml(data) {
      data = data || state.lastData;
      if (!data) return '';

      // Окно — последние 7 дней (= «Неделя» на /setups), единое для всех тикеров.
      // Per-ticker дату в заголовок НЕ выносим: она создавала несогласованность
      // («12 июня» у одних тикеров vs «за сегодня» у пустых). Дата каждого сетапа
      // видна в самой строке списка. Заголовок одинаков для всех.
      var titleHtml = '<h3>СЕТАПЫ'
        + ' <span class="trig-date">· за неделю</span></h3>';
      var header = '<div class="trig-side-header">' + titleHtml + '</div>';

      // Триггеры показываются на всех TF, включая D1 (intraday-triggers snap'аются
      // на D1-bar того же дня — backend готовит timestamp в _compute_ta_chart_data).
      var body = _renderTriggerList(data.triggers || [],
                                    {emptyMsg: 'За неделю сетапов нет'});
      // Аноним видит сетапы с задержкой (та же, что на витрине) — говорим об этом
      // прямо, иначе короткий список читается как «сетапов не было». Бесплатному после
      // триала сервер добавляет anon_gated_reason = 'subscription' (15.09.2026): он уже
      // вошёл, поэтому причина — подписка, а клик открывает оплату (window.openPaywall из
      // PAYWALL_JS; на странице без окна оплаты — витрина с ?paywall=delayed).
      var gate = '';
      if (data.anon_gated && data.anon_gated_reason === 'subscription') {
        gate = '<div class="trig-gate tone-accent" onclick="'
             + "if(window.openPaywall){window.openPaywall({source:'delayed'})}else{location.href='/setups?paywall=delayed'}"
             + '">🔒 Свежие сетапы — по подписке'
             + '<span class="trig-gate-cta">→ Оформить подписку</span></div>';
      } else if (data.anon_gated) {
        gate = '<div class="trig-gate" onclick="'
             + "location.href='/api/auth/yandex/start?next='+encodeURIComponent(location.pathname+location.search)"
             + '">🔒 Свежие сетапы — после регистрации'
             + '<span class="trig-gate-cta">→ Войти через Яндекс</span></div>';
      }
      return header + gate + body;
    }

    // ── Regime badge в шапке графика (desktop + mobile одинаково) ──
    function buildRegimeBadgeHtml(data) {
      data = data || state.lastData;
      if (!data) return '<span class="regime-badge" id="regimeBadge"></span>';
      var rg = (data.meta && data.meta.regime) || {regime: 'RANGE'};
      var rgCls = _REGIME_CLS[rg.regime] || 'muted';
      return '<span class="regime-badge ' + rgCls + '" id="regimeBadge" title="Режим рынка">'
        + '<span class="regime-badge-lbl">Режим рынка</span>'
        + '<span class="regime-badge-val">' + _esc(rg.regime) + '</span>'
        + '</span>';
    }

    // ── Public API ──
    return {
      load: function(ticker, tf, around) {
        if (tf) state.currentTf = tf;
        state.currentTicker = ticker;
        state.extraMarker = null;   // сброс доп.маркера при смене тикера/TF
        // Бэктест-витрина: окно свечей на историческую дату + подавление live-price
        // (исторический срез — текущая цена не должна дорисовываться к last bar).
        state.backtestMode = !!around;
        var url = state.dataUrlBase + ticker + '?tf=' + state.currentTf + (state.extraQuery || '');
        if (around) url += '&around=' + encodeURIComponent(around);

        /** Parse JSON ответ → либо данные либо {_failed:true, error}. */
        function parseResponse(r) {
          return r.json().then(function(data) {
            if (!r.ok || data.error) {
              return { error: data.error || ('HTTP ' + r.status), _failed: true };
            }
            return data;
          });
        }

        function applyResize() {
          requestAnimationFrame(function() {
            if (state.chart) {
              try { state.chart.resize(chartEl.clientWidth, chartEl.clientHeight); } catch(e) {}
            }
          });
        }

        /** Lazy-load: preview (~48KB, ~300ms) + full (~1.15MB, ~1.5s) параллельно.
         *  Preview рендерится первым — пользователь видит chart за ~350-700ms
         *  даже на медленных сетях. Full догружается и заменяет данные с EMA +
         *  trigger markers + полной историей баров.
         *
         *  ВАЖНО: preview НЕ содержит RSI data. Когда state.showRsi=true (юзер
         *  выбрал DIVERGENCE filter), preview render создаёт chart БЕЗ RSI pane,
         *  затем full render делает state.chart.remove() и пересоздаёт chart
         *  С RSI pane. Это **две тяжёлые LWC init подряд** (~500ms каждая на
         *  медленном устройстве) + визуальный flicker (RSI появляется на 2-м рендере).
         *  Skip preview когда RSI нужен — ждём только full, экономим 500ms+ и
         *  убираем flicker.
         */
        var fullPromise    = fetch(url).then(parseResponse);
        var previewPromise = (state.showRsi || around)
          ? Promise.resolve({_skipped: true})
          : fetch(url + '&preview=1').then(parseResponse);

        // Рендерим preview как только пришёл (race с full: если full уже успел
        // прийти первым, preview не перерисовываем — данные хуже).
        previewPromise.then(function(data) {
          if (data && data._skipped) return;              // skipped когда showRsi=true
          if (state.currentTicker !== ticker) return;     // user switched ticker
          if (data._failed) return;                       // preview error: ждём full, он сам покажет ошибку
          // full уже отрендерён? Признаки: meta.preview не выставлен ИЛИ есть EMA series
          var isFull = state.lastData && state.lastData.meta && !state.lastData.meta.preview;
          if (isFull) return;
          state.lastData = data;
          renderChart(data);
          applyResize();
        }).catch(function(e) { /* swallow — full path is authoritative */ });

        // Финальный render — полный payload (заменяет preview).
        return fullPromise.then(function(data) {
          if (state.currentTicker !== ticker) return data;  // user switched ticker
          if (data._failed) {
            // Если preview уже отрендерён — оставляем его (lite-режим лучше пустого экрана).
            // Чистим только если preview тоже зафейлился (state.lastData ещё null).
            if (!state.lastData) {
              if (state.candleSeries) {
                try { state.candleSeries.setData([]); } catch(e) {}
                try { state.candleSeries.setMarkers([]); } catch(e) {}
              }
              if (state.emaSeries) {
                try { state.emaSeries.ema50 && state.emaSeries.ema50.setData([]); } catch(e) {}
                try { state.emaSeries.ema200 && state.emaSeries.ema200.setData([]); } catch(e) {}
              }
            }
            // Явный маркер ошибки — иначе внешний скриншотер просто ждал бы
            // __CHART_READY__ до таймаута, не понимая причины.
            try { window.__CHART_ERROR__ = String(data.error || 'load failed'); } catch (e) {}
            return data;
          }
          state.lastData = data;
          renderChart(data);
          applyResize();
          // Сигнал «график дорисован» для внешнего скриншотера (setup_screenshot.py).
          // С задержкой: подписи уровней доседают через rAF + setTimeout(200/700) в
          // _renderSRLabels, а живая цена приходит асинхронно — снимок раньше поймал бы
          // съехавшие подписи (тот самый баг «уехавших подписей» от 29.06).
          // На сайте флаг никто не читает — поведение страниц не меняется.
          try {
            window.__CHART_READY__ = false;
            setTimeout(function() { window.__CHART_READY__ = true; }, 850);
          } catch (e) {}
          return data;
        });
      },
      // Состояние слоя — нужно шапке, чтобы ПРИВЕСТИ движок к своим кнопкам, а не
      // слепо переключить. Без этого метода потребители падали на fallback `!on`,
      // который всегда ≠ on → клик переключал слой в обратную сторону (симптом:
      // «MTF не нажат, но уровни есть; нажимаю — пропадают»).
      getLayerState: function(layer) { return !!(state.layers || {})[layer]; },
      toggleLayer: function(layer) {
        state.layers[layer] = !state.layers[layer];
        if (state.lastData) renderChart(state.lastData);
        return state.layers[layer];
      },
      getLayer: function(layer) { return state.layers[layer]; },

      /** Доп. query-строка к chart-data URL (например '&min_er_lead=0.2').
       *  Берётся при следующем load(). */
      setExtraQuery: function(q) { state.extraQuery = q || ''; },
      // Источник данных (14.09.2026, /ml-setups «Фьючерсы»: свечи контракта из
      // /api/ml-setups/fut-chart/ вместо акции). Пусто → стандартный chart-data акций.
      setDataUrlBase: function(b) { state.dataUrlBase = b || '/api/ta/chart-data/'; },

      /** Установить whitelist trigger_types (DB-format strings, например
       *  'STOP_HUNT_MTF'). Принимает Set, Array или null/undefined (= показать
       *  все типы). Если chart уже отрисован — markers обновляются мгновенно
       *  без перезагрузки данных. */
      setTriggerTypes: function(types) {
        if (types == null) {
          state.triggerTypes = null;
        } else if (types instanceof Set) {
          state.triggerTypes = types.size > 0 ? types : null;
        } else if (Array.isArray(types)) {
          state.triggerTypes = types.length > 0 ? new Set(types) : null;
        } else {
          state.triggerTypes = null;
        }
        if (state.candleSeries) _applyTriggerFilter();
      },
      getTriggerTypes: function() { return state.triggerTypes; },

      /** Горизонтальные price-линии (для /admin/waves: вход/стоп/цель/выход).
       *  lines = [{price, color, title}]. Перерисовывает поверх существующих
       *  wave-линий. Аддитивно — не трогает остальной рендер. */
      setPriceLines: function(lines, opts) {
        if (state._waveLines) {
          state._waveLines.forEach(function(l){ try { state.candleSeries.removePriceLine(l); } catch(e){} });
        }
        state._waveLines = [];
        if (!state.candleSeries || !lines) return;
        // Опционально включить все уровни в диапазон Y (кадры рилсов,
        // 03.11.2026). Без opts.fit поведение существующих страниц прежнее.
        if (opts && opts.fit) {
          var fitPrices = lines.map(function(ln) { return Number(ln.price); }).filter(isFinite);
          state.candleSeries.applyOptions({autoscaleInfoProvider: function(base) {
            var info = base();
            if (!info || !info.priceRange || !fitPrices.length) return info;
            return Object.assign({}, info, {priceRange: {
              minValue: Math.min.apply(Math, [info.priceRange.minValue].concat(fitPrices)),
              maxValue: Math.max.apply(Math, [info.priceRange.maxValue].concat(fitPrices))
            }});
          }});
        }
        lines.forEach(function(ln){
          state._waveLines.push(state.candleSeries.createPriceLine({
            price: ln.price, color: ln.color, lineWidth: 1,
            lineStyle: (ln.lineStyle != null ? ln.lineStyle : 2),   // 0=сплошная (SL), 2=пунктир (дефолт)
            axisLabelVisible: ln.axisLabelVisible !== false, title: ln.title || ''
          }));
        });
      },

      /** Подсветить ключевой уровень выбранного сетапа: совпадающая по цене MTF-линия+подпись
       *  («PDH·7» и т.п.) рисуются ярче ЦВЕТОМ (#ffd24a, full opacity; толщина та же 1px) +
       *  подпись жирнее+рамка (БЕЗ дубль-линии «Уровень»). price=null — снять подсветку.
       *  Перерисовывает линию + подпись без reload (для same-ticker выбора). */
      setHighlightLevel: function(price) {
        state.highlightLevel = (price != null && isFinite(Number(price))) ? Number(price) : null;
        try { _drawMtfLines(); } catch(e) {}                              // линия (ярче цветом, 1px)
        if (state._srLabelsUpdate) { try { state._srLabelsUpdate(); } catch(e) {} }   // подпись
      },

      /** Solo-режим (/setups): показать ТОЛЬКО выбранный сетап (вход+выход, точно
       *  по цене). key = {bar_date, trigger, direction, tf} (DB-format) либо null
       *  (= все маркеры, поведение /ta). Если chart отрисован — обновит мгновенно. */
      /** Триггер, построенный ПОТРЕБИТЕЛЕМ (строка /setups), которого нет в
       *  chart-data этого ТФ — 4ч-сетап на 1ч/15м/5м графике (сервер отдаёт
       *  4ч-маркеры только на 4ч). Кладём в triggersData (дедуп по бару + типу +
       *  стороне + ТФ), дальше solo рисует его как любой другой: стрелку на баре
       *  входа (soloEntryBar по его tf) и свой тултип. Возвращает true, если добавлен. */
      addSoloTrigger: function(t) {
        if (!t || t.bar_date == null || !t.trigger) return false;
        var bd = String(t.bar_date).substring(0, 16), tfv = String(t.tf || '1h');
        var has = (state.triggersData || []).some(function (x) {
          return String(x.bar_date || '').substring(0, 16) === bd && x.trigger === t.trigger
              && (t.direction == null || x.direction === t.direction)
              && String(x.tf || '1h') === tfv;
        });
        if (has) return false;
        state.triggersData = (state.triggersData || []).concat([t]);
        return true;
      },

      setSoloTrigger: function(key, soloExit) {
        state.soloKey = key || null;
        // Клиентский exit-маркер выбранного TOP-сетапа (user-режим: chart-data не шлёт
        // exit_markers; у admin они уже есть из chart-data). Добавляем в state.exitMarkers,
        // если ещё нет (dedup по bar_date+trigger) → _renderSoloMarkers нарисует квадрат.
        if (soloExit && soloExit.bar_date != null) {
          var _bd = String(soloExit.bar_date).substring(0, 16);
          var _has = (state.exitMarkers || []).some(function (x) {
            return String(x.bar_date || '').substring(0, 16) === _bd && x.trigger === soloExit.trigger
                && String(x.tf || '1h') === String(soloExit.tf || '1h');   // 1ч и 4ч на общем баре — разные выходы
          });
          if (!_has) state.exitMarkers = (state.exitMarkers || []).concat([soloExit]);
        }
        if (state.candleSeries) _applyTriggerFilter();
      },

      /** Instant clear всех series — для UX feedback при смене тикера.
       *  Вызывается ДО engine.load() чтобы юзер не видел stale данные
       *  предыдущего тикера в течение fetch'a (несколько секунд). */
      clearData: function() {
        try {
          if (state.candleSeries) {
            state.candleSeries.setData([]);
            state.candleSeries.setMarkers([]);
          }
          if (state.ema50Series) state.ema50Series.setData([]);
          if (state.ema200Series) state.ema200Series.setData([]);
          if (state.volumeSeries) state.volumeSeries.setData([]);
          if (state.rsiSeries) state.rsiSeries.setData([]);
          // RSI HTML label
          var oldLabel = chartEl.querySelector('.rsi-pane-label');
          if (oldLabel) oldLabel.remove();
          // Divergence series (line series, не price-line)
          _clearDivergence();
          _clearAllDoubleExtreme();
          state.deHiddenKeys = {};   // новые данные → все зигзаги снова показаны
          // Price lines — remove все S/R + Fib + live price
          if (state.priceLinesArr && state.candleSeries) {
            state.priceLinesArr.forEach(function(pl) {
              try { state.candleSeries.removePriceLine(pl); } catch(e) {}
            });
          }
          state.priceLinesArr = [];
          // MTF-линии (отдельный массив) — тоже снять при смене тикера
          if (state._mtfLines && state.candleSeries) {
            state._mtfLines.forEach(function(pl) {
              try { state.candleSeries.removePriceLine(pl); } catch(e) {}
            });
          }
          state._mtfLines = [];
          // SL / уровень (setPriceLines) — снять при смене тикера, иначе линия висит
          if (state._waveLines && state.candleSeries) {
            state._waveLines.forEach(function(l) {
              try { state.candleSeries.removePriceLine(l); } catch(e) {}
            });
          }
          state._waveLines = [];
          if (state.livePriceLine && state.candleSeries) {
            try { state.candleSeries.removePriceLine(state.livePriceLine); } catch(e) {}
            state.livePriceLine = null;
          }
          state.triggersData = [];
          state.lastData = null;
        } catch (e) {
          // Игнорируем — clearData best-effort
        }
      },

      /** Программный выбор триггера (для вызова из side panel / setups table /
       *  другого UI). Idempotent: если такая же divergence уже нарисована —
       *  no-op (не toggle off). Toggle-семантика для marker-click осталась
       *  в _drawDivergence (subscribeClick handler).
       *  trig = null → очищает (используется при переключении на non-DIVERGENCE). */
      selectTrigger: function(trig) {
        if (trig && trig.divergence) {
          var key = trig.bar_date || '';   // DE-зигзаги независимы — не трогаем
          if (state.selectedDivergenceKey === key) {
            return;   // already drawn for this key — idempotent no-op
          }
          _drawDivergence(trig);
        } else if (trig && trig.double_extreme) {
          _clearDivergence();
          // Зигзаг показан по умолчанию; гарантируем видимость (unhide + draw idempotent).
          delete state.deHiddenKeys[trig.bar_date || ''];
          _drawDoubleExtremeKey(trig);
        } else {
          _clearDivergence();   // trig=null → снять divergence; DE-зигзаги ведёт sync
        }
      },

      /** Доп. маркер по unix-времени (для /ml-setups: показать кликнутый
       *  ре-детект-триггер, которого нет в trigger_sent_log → нет в chart-data).
       *  time=null снимает. position/shape/color как у обычных маркеров. */
      setExtraMarker: function(time, position, shape, color) {
        state.extraMarker = (time == null) ? null
          : {time: time, position: position || 'aboveBar', shape: shape || 'circle',
             color: color || '#57a5ff', text: '', size: 2};
        _applyTriggerFilter();
      },

      /** Бэктест-витрина: показать ТОЛЬКО ОДНУ историческую сделку (вход+выход)
       *  из pickle, независимо от live-триггеров графика. Инжектируем сделку как
       *  единственный solo-триггер → переиспользуем _renderSoloMarkers (snap к
       *  реальной свече + квадрат выхода). trade=null снимает.
       *  opts (опц., /ml-setups) — /setups его НЕ передаёт → поведение прежнее:
       *    entryAboveBelow — стрелка входа над/под баром сигнала (вместо inBar-по-цене);
       *    entryMark       — короткая линия входа на баре входа (open след. бара);
       *    exitLine        — короткая линия выхода вместо квадрата;
       *    entryTip        — 3-я строка тултипа стрелки («Вход <цена>»). */
      setBacktestTrade: function(trade, opts) {
        if (!state.chart) return;
        if (!trade) { state.soloKey = null; state.triggersData = []; state.exitMarkers = []; _applyTriggerFilter(); return; }
        var op = opts || {};
        var isLong = trade.direction === 'LONG';
        var exitColor = '#ff2bd6';   // маджента — видна на зелёных И красных свечах (знак R не цветом)
        // Тултип выхода — как admin chart-data _tip + TOP-карточка RR:
        // «Выход по трейлу 109.0 / RR ~1:5» (прибыль) | «Выход по стопу 96.8» (стоп, без RR).
        // TRAIL_TIME ≠ TRAIL: трейл взведён, но закрыло истечение окна (close последнего
        // бара), а не стоп. Подпись «трейлу» врала — фикс 2026-07-18.
        var _exRu = {TP: 'тейку', TRAIL: 'трейлу',
                     TRAIL_TIME: 'истечению окна (трейл взведён)', SL: 'стопу',
                     EXPIRED: 'истечению окна', BE: 'безубытку',
                     // портфельный слой закрыл позицию встречным сигналом (переворот)
                     PF_EXIT: 'перевороту',
                     // позицию закрыли накануне дивидендной отсечки (правило 30.08)
                     DIV: 'закрытию перед дивидендной отсечкой',
                     // «Разворот рынка» (галочка админа /setups, обкатка №188, 16.10.2026)
                     MKT: 'развороту рынка'}[trade.r_outcome] || 'выходу';
        var _exP = Number(trade.exit_price);
        var _exPx = !isFinite(_exP) ? '' : (_exP >= 100 ? _exP.toFixed(1) : _exP >= 1 ? _exP.toFixed(2)
                                            : _exP >= 0.01 ? _exP.toFixed(4) : _exP.toFixed(6));
        // Двухфазные детекторы (climax, fb_mtf) взводят трейл принудительно после
        // phase1_bars, цель при этом могла не достигаться — тогда «по трейлу» без
        // оговорки вводит в заблуждение. tp_reached===false говорит об этом явно;
        // null (не проверяли) оставляет прежнюю подпись.
        // ⚠ Номер свечи НЕ константа: 1ч=20, 4h=5 — берём из самой сделки
        // (ml_setups_data._phase1_bars ← TF_DEFAULTS детектора), иначе на 4h соврём.
        if (trade.r_outcome === 'TRAIL' && trade.tp_reached === false && trade.phase1_bars)
          _exRu = 'трейлу с ' + trade.phase1_bars + '-й свечи';
        var _exTip = ('Выход по ' + _exRu + ' ' + _exPx).replace(/\s+$/, '');
        if (trade.r != null && trade.r > 0) _exTip += ' / RR ~1:' + Math.max(1, Math.round(trade.r));
        // Оговорка к выходу, если страница поясняет его происхождение (exit_note). До 14.09
        // /ml-setups «Фьючерсы» писала здесь «на фьючерсе, цена в ценах акции» — теперь там
        // рисуется сам контракт, и оговорка не нужна.
        if (trade.exit_note) _exTip += ' · ' + trade.exit_note;
        state.extraMarker = null;
        state.triggersData = [{
          bar_date: trade.entry_bar_date, trigger: trade.trigger_type, direction: trade.direction,
          time: trade.entry_time, entry_price: trade.entry_price,
          shape: isLong ? 'arrowUp' : 'arrowDown',
          // Цвет стрелки = цвет имени сетапа в таблице (по типу), не по направлению —
          // направление видно по форме (вверх/вниз) и полосе строки.
          color: trade.color || (isLong ? '#3fb84f' : '#f75049'),
          // Тултип стрелки (/ml-setups): имя/описание/«Вход <цена>». /setups не задаёт.
          tip_title: trade.tip_title, tip: trade.tip,
          _entryAboveBelow: !!op.entryAboveBelow, _entryMark: !!op.entryMark, _entryTip: op.entryTip || null,
          // market-вход по open следующего бара (не лимитка) + длительность сигнального бара
          _entryAtOpen: !!op.entryAtOpen, _signalBarSec: op.signalBarSec || 3600,
          // Явный якорь линии входа (unix). Нет → firstAfter(сигнал), как на /ml-setups.
          _entryMarkTime: (op.entryMarkTime != null ? op.entryMarkTime : null)
        }];
        var _exit = (trade.exit_time != null && trade.exit_price != null) ? {
          bar_date: trade.entry_bar_date, trigger: trade.trigger_type, direction: trade.direction,
          time: trade.exit_time, exit_price: trade.exit_price, color: exitColor, tip: _exTip,
          // точный момент выхода (unix), если страница его знает — см. exitMomentTime
          exit_at: (typeof trade.exit_at === 'number' ? trade.exit_at : null)
        } : null;
        // /ml-setups: короткая линия выхода + чистый тултип «Выход …» (ветка t.exit).
        // /setups (op.exitLine отсутствует) — квадрат + прежняя ветка тултипа.
        if (_exit && op.exitLine) { _exit._exitLine = true; _exit.exit = true; }
        state.exitMarkers = _exit ? [_exit] : [];
        state.soloKey = {bar_date: trade.entry_bar_date, trigger: trade.trigger_type, direction: trade.direction};
        _renderSoloMarkers();
      },

      /** Returns last loaded chart-data (response от /api/ta/chart-data).
       *  Используется внешним кодом для lookup'а divergence-trigger object'а
       *  без повторной перезагрузки графика (см. setups.html table-click). */
      getLastData: function() { return state.lastData; },

      /** Toggle RSI pane visibility (для гейтинга по DIVERGENCE фильтру).
       *  Re-render chart с новой compactовкой панелей. No-op если состояние не изменилось. */
      setShowRsi: function(show) {
        var newVal = !!show;
        if (newVal === state.showRsi) return;
        state.showRsi = newVal;
        if (state.lastData) renderChart(state.lastData);
      },
      getShowRsi: function() { return state.showRsi; },

      /** HTML правой панели (desktop) — режим + триггеры за последние 2 часа */
      getTrigSideHtml:    function() { return buildTrigSideHtml(); },

      /** HTML компактного badge'а режима для шапки графика (mobile) */
      getRegimeBadgeHtml: function() { return buildRegimeBadgeHtml(); },

      /** HTML переключателя таймфреймов (D1/1H) — кнопки с data-tf */
      getTfSwitcherHtml: function() {
        var tfs = ['D1', '1h', '15m', '5m'];
        var html = '';
        for (var i = 0; i < tfs.length; i++) {
          var tf = tfs[i];
          var cls = 'tf-btn' + (tf === state.currentTf ? ' active' : '');
          html += '<button class="' + cls + '" data-tf="' + tf + '">' + tf + '</button>';
        }
        return html;
      },

      /** HTML тогглов слоёв (S/R, Fib, EMA) — кнопки с data-layer */
      getLayerTogglesHtml: function() {
        var layers = [['sr','S/R'], ['fib','Fib'], ['ema','EMA'], ['mtf','MTF']];
        var html = '';
        for (var i = 0; i < layers.length; i++) {
          var l = layers[i][0], lbl = layers[i][1];
          var cls = 'layer-btn' + (state.layers[l] ? ' active' : '');
          html += '<button class="' + cls + '" data-layer="' + l + '">' + lbl + '</button>';
        }
        return html;
      },

      /** Биндит клики на кнопки [data-tf] и [data-layer] внутри rootEl.
       *  При клике сам обновляет active-класс и вызывает соответствующий API.
       *  Принимает опциональный onTfChange callback (после load — для шапки) */
      bindHeaderControls: function(rootEl, onTfChange) {
        var self = this;
        rootEl.querySelectorAll('[data-tf]').forEach(function(btn) {
          btn.addEventListener('click', function() {
            var tf = btn.getAttribute('data-tf');
            rootEl.querySelectorAll('[data-tf]').forEach(function(b) {
              b.classList.toggle('active', b === btn);
            });
            // Re-load с новым tf — нужен ticker; берём из lastData
            var ticker = state.lastData && state.lastData.meta ? state.lastData.meta.ticker : null;
            if (ticker) {
              self.load(ticker, tf).then(function(data) {
                if (typeof onTfChange === 'function') onTfChange(data);
              });
            }
          });
        });
        rootEl.querySelectorAll('[data-layer]').forEach(function(btn) {
          btn.addEventListener('click', function() {
            var layer = btn.getAttribute('data-layer');
            var active = self.toggleLayer(layer);
            btn.classList.toggle('active', active);
          });
        });
      },

      /** HTML списка триггерных блоков — для bottom sheet (mobile) */
      renderTriggerBlocks: function(triggers) { return _renderTriggerList(triggers); },

      getData: function() { return state.lastData; },
      getTf: function() { return state.currentTf; },
      resize: function() {
        if (state.chart) state.chart.resize(chartEl.clientWidth, chartEl.clientHeight);
      },

      /** Override visible range: показать `daysSpan` суток истории, ПЛЮС
       *  `rightPadDays` суток пустого пространства справа (последний бар
       *  отступает на rightPadDays от правой цен.шкалы — даёт «воздух»).
       *
       *  setVisibleDays(9, 1) → видимое окно = 9 суток, последний бар на
       *  позиции 8/9 от левого края (1 сутки пустого справа).
       *
       *  Реализация через setVisibleLogicalRange (bar-based) — у LWC bug:
       *  setVisibleRange клампит `to` к фактическому last data point,
       *  игнорируя future-time padding. Logical range принимает индексы
       *  > lastIdx и рендерит за ними пустоту. /ta не затронут. */
      setVisibleDays: function(daysSpan, rightPadDays) {
        if (!state.chart || !state.lastData || !state.lastData.candles) return;
        var candles = state.lastData.candles;
        if (!candles.length) return;
        // Approximate trading bars per calendar day (для 1H — 14 торговых
        // часов в МСК; для D1 — 1 бар; weekends внутри окна — see below).
        var barsPerDay = state.currentTf === '4h' ? 4.4
                       : state.currentTf === '1h' ? 14
                       : state.currentTf === '15m' ? 56
                       : state.currentTf === '5m' ? 168 : 1;
        var totalBars  = Math.max(1, Math.round(daysSpan * barsPerDay));
        // rightPadDays не передан → общий отступ RIGHT_OFFSET_BARS (в барах).
        var padBars    = (rightPadDays == null)
          ? RIGHT_OFFSET_BARS
          : Math.max(0, Math.round(rightPadDays * barsPerDay));
        var lastIdx    = candles.length - 1;
        // Logical from/to: история занимает (totalBars - padBars) баров,
        // справа от lastIdx остаётся padBars пустых позиций.
        state.chart.timeScale().setVisibleLogicalRange({
          from: lastIdx - totalBars + padBars + 1,
          to:   lastIdx + padBars
        });
      },

      /** Override visible range через фиксированное число баров (не зависит от TF).
       *  Используется в /signals — там надо одинаковую визуальную плотность
       *  свечей независимо от выбранного TF (D1 / 1H) и режима expand.
       *  barsSpan = total visible bars (история + правый padding).
       *  rightPadBars = пустое пространство справа в барах. */
      setVisibleBars: function(barsSpan, rightPadBars) {
        if (!state.chart || !state.lastData || !state.lastData.candles) return;
        var candles = state.lastData.candles;
        if (!candles.length) return;
        var totalBars = Math.max(1, Math.round(barsSpan));
        // rightPadBars не передан → общий дефолт RIGHT_OFFSET_BARS (единый отступ
        // справа на всех графиках). Явно переданное значение уважаем как есть.
        var padBars   = Math.max(0, Math.round(
          rightPadBars == null ? RIGHT_OFFSET_BARS : rightPadBars));
        var lastIdx   = candles.length - 1;
        state.chart.timeScale().setVisibleLogicalRange({
          from: lastIdx - totalBars + padBars + 1,
          to:   lastIdx + padBars
        });
      },

      /** Центрировать видимое окно на свече, ближайшей к unix-времени `t` (solo-
       *  просмотр выбранного сетапа: маркер по центру по горизонтали, чтобы не
       *  скроллить). half = баров с каждой стороны. */
      centerOnTime: function(t, half) {
        if (!state.chart || !state.lastData || !state.lastData.candles || t == null) return;
        var candles = state.lastData.candles;
        if (!candles.length) return;
        half = Math.max(20, Math.round(half || 60));
        var idx = 0, best = Infinity;
        for (var i = 0; i < candles.length; i++) {
          var d = Math.abs((candles[i].time || 0) - t);
          if (d < best) { best = d; idx = i; }
        }
        state.chart.timeScale().setVisibleLogicalRange({ from: idx - half, to: idx + half });
      },
      destroy: function() {
        if (state.liveInterval) { clearInterval(state.liveInterval); state.liveInterval = null; }
        if (state.liveVisHandler) {
          document.removeEventListener('visibilitychange', state.liveVisHandler);
          state.liveVisHandler = null;
        }
        if (state.chart) { state.chart.remove(); state.chart = null; }
        chartEl.innerHTML = '';
        state.lastData = null;
        state.currentTicker = null;
      },
    };
  }

  return {create: create, fmtPrice: _fmtPrice};
})();
